export const ASTROVA_STORAGE_KEY = "astrova.gameState.v1";
export const TICK_INTERVAL_MS = 50;
export const AUTO_SAVE_INTERVAL_MS = 30 * 1000;
export const MAX_OFFLINE_SECONDS = 24 * 60 * 60;

export type ProbeSpecKey = "engine" | "explorationGear" | "shield" | "antenna";
export type PlanetGrade = "F" | "C" | "B" | "A" | "S" | "X";
export const PLANET_TYPES = ["ice", "lava", "gas", "desert", "ocean", "forest", "alien"] as const;
export type PlanetType = typeof PLANET_TYPES[number];
export type ExpeditionStatus = "idle" | "traveling" | "exploring" | "returning" | "completed" | "failed";
export type ResearchStatus = "locked" | "available" | "researching" | "completed";
export type ResearchNodeId =
    | "basic_exploration"
    | "planet_analysis"
    | "high_speed_engine"
    | "deep_space_exploration"
    | "life_detection"
    | "artifact_scanner"
    | "spectral_mapping"
    | "mineral_survey"
    | "adaptive_drilling"
    | "thermal_refinery"
    | "quantum_harvest"
    | "planetary_cartography"
    | "stellar_logistics"
    | "matter_reclaimer"
    | "cosmic_extraction"
    | "terraforming_index"
    | "ion_thrusters"
    | "trajectory_ai"
    | "gravity_slingshot"
    | "warp_calibration"
    | "subspace_sails"
    | "tachyon_drive"
    | "relativistic_navigation"
    | "zero_point_engine"
    | "event_horizon_drive"
    | "lightfold_engine"
    | "hazard_prediction"
    | "void_signal_array"
    | "bio_signature_archive"
    | "ancient_translation"
    | "xeno_ecology"
    | "relic_resonator"
    | "deep_space_beacon"
    | "multiverse_sensor"
    | "forbidden_atlas"
    | "origin_signal"
    | "biosphere_simulation"
    | "orbital_foundry"
    | "dark_matter_sieve"
    | "stellar_compression"
    | "nano_probe_swarm"
    | "antimatter_logistics"
    | "proto_world_engine"
    | "genesis_harvest"
    | "photon_lattice"
    | "inertial_nullifier"
    | "wormhole_geometry"
    | "causality_buffer"
    | "quantum_wake_drive"
    | "horizon_skimming"
    | "singularity_pilot"
    | "eternity_engine"
    | "sentient_signal_model"
    | "relic_memory_core"
    | "living_world_index"
    | "mythic_archive"
    | "first_contact_protocol"
    | "precursor_gateway"
    | "cosmic_language"
    | "omega_observatory";

export interface CountedResourceState {
    amount: number;
    lifetimeEarned: number;
    lifetimeSpent: number;
}

export interface EnergyResourceState {
    current: number;
    capacity: number;
    lifetimeEarned: number;
    lifetimeSpent: number;
    lastRecoveredAt: string;
}

export interface ResourceState {
    explorationData: CountedResourceState;
    energy: EnergyResourceState;
    parts: CountedResourceState;
}

export interface ResourceAmounts {
    explorationData: number;
    energy: number;
    parts: number;
}

export interface ProbeSpecState {
    level: number;
    spentParts: number;
}

export interface EffectiveStats {
    explorationSpeedMultiplier: number;
    resourceGainMultiplier: number;
    survivalPower: number;
    rareDiscoveryChanceBonus: number;
    lifeDiscoveryChanceBonus: number;
    deepSpaceUnlocked: boolean;
}

export interface ProbeState {
    probeId: string;
    specs: Record<ProbeSpecKey, ProbeSpecState>;
    cachedEffectiveStats: EffectiveStats;
}

export interface PlanetHazard {
    type: "radiation" | "gravity_anomaly" | "hostile_life";
    severity: number;
    requiredShieldPower: number;
}

export interface ArtifactSlot {
    artifactKey: "ancient_ruins" | "black_box" | "crystal";
    rarity: "uncommon" | "rare" | "epic" | "legendary";
    discovered: boolean;
}

export interface TargetPlanet {
    planetId: string;
    seed: string;
    name: string;
    grade: PlanetGrade;
    type: PlanetType;
    distanceTier: number;
    lifeSignal: "none" | "microbial" | "intelligent";
    hazards: PlanetHazard[];
    artifactSlots: ArtifactSlot[];
}

export interface ExpeditionProgress {
    current: number;
    required: number;
}

export interface FailureState {
    reason: "insufficient_shield";
    requiredShieldPower: number;
    survivalPower: number;
    lostParts: number;
    failedAt: string;
}

export interface CurrentExpeditionState {
    status: ExpeditionStatus;
    startedAt: string;
    lastTickAt: string;
    expectedCompletedAt: string;
    progress: ExpeditionProgress;
    targetPlanet: TargetPlanet | null;
    pendingRewards: ResourceAmounts & { artifactInstanceIds: string[] };
    failureState: FailureState | null;
}

export interface DiscoveredPlanetRecord {
    planetId: string;
    seed: string;
    name: string;
    grade: PlanetGrade;
    type: TargetPlanet["type"];
    lifeSignal: TargetPlanet["lifeSignal"];
    hazards: PlanetHazard["type"][];
    discoveredAt: string;
    completedAt: string | null;
    outcome: "completed" | "failed" | "in_progress";
    earnedResources: ResourceAmounts;
    artifactInstanceIds: string[];
}

export interface ArtifactRecord {
    artifactInstanceId: string;
    artifactKey: ArtifactSlot["artifactKey"];
    rarity: ArtifactSlot["rarity"];
    sourcePlanetId: string;
    foundAt: string;
    prestigeStageFound: number;
}

export interface ArtifactSummary {
    artifactKey: ArtifactSlot["artifactKey"];
    count: number;
    firstFoundAt: string | null;
    lastFoundAt: string | null;
    sourcePlanetIds: string[];
}

export interface ArchiveState {
    discoveredPlanets: {
        byId: Record<string, DiscoveredPlanetRecord>;
        order: string[];
    };
    artifactCollection: {
        byInstanceId: Record<string, ArtifactRecord>;
        summaryByArtifactKey: Partial<Record<ArtifactSlot["artifactKey"], ArtifactSummary>>;
    };
}

export interface ResearchEffects {
    resourceGainMultiplier: number;
    explorationSpeedMultiplier: number;
    deepSpaceUnlocked: boolean;
    lifeDiscoveryChanceBonus: number;
    artifactDiscoveryChanceBonus: number;
}

export interface ResearchNodeState {
    status: ResearchStatus;
    level: number;
    maxLevel: number;
    spentExplorationData: number;
    unlockedAt: string | null;
    completedAt: string | null;
}

export interface ResearchState {
    treeVersion: number;
    nodes: Record<ResearchNodeId, ResearchNodeState>;
    activeEffects: ResearchEffects;
}

export interface PrestigeBonuses {
    explorationSpeedMultiplier: number;
    resourceGainMultiplier: number;
    artifactDiscoveryChanceBonus: number;
    xGradeUnlocked: boolean;
}

export interface PrestigeState {
    currentStage: number;
    currentStarSystemKey: string;
    resetCount: number;
    completedStarSystems: string[];
    currentStageProgress: {
        requiredPlanetClears: number;
        clearedPlanetCount: number;
        sGradeCleared: boolean;
    };
    permanentBonuses: PrestigeBonuses;
    history: Array<{
        stage: number;
        starSystemKey: string;
        prestigedAt: string;
        grantedBonuses: Partial<PrestigeBonuses>;
    }>;
}

export type GuideQuestCategoryId =
    | "exploration_intro"
    | "probe_upgrade"
    | "research"
    | "artifact"
    | "deep_space"
    | "prestige"
    | "challenge";

export interface GuideQuestFlags {
    expeditionStarted: boolean;
    researchTreeOpened: boolean;
    artifactVaultOpened: boolean;
    prestigeTabOpened: boolean;
    prestigeConditionChecked: boolean;
    offlineReturn24h: boolean;
}

export interface GuideQuestStats {
    dailyUpgradeDate: string | null;
    dailyUpgradeCount: number;
    maxDailyUpgradeCount: number;
    maxOfflinePartsReward: number;
    researchTreesCompleted: number;
    currentRunResearchTreeCompleted: boolean;
    multiversePlanetsCompleted: number;
}

export interface GuideQuestState {
    completedQuestIds: number[];
    completedTitles: string[];
    flags: GuideQuestFlags;
    stats: GuideQuestStats;
}

export interface OfflineReport {
    from: string;
    to: string;
    elapsedSeconds: number;
    planetsDiscovered: number;
    resourcesGained: ResourceAmounts;
    artifactInstanceIds: string[];
}

export interface OfflineState {
    lastAccessedAt: string;
    lastSavedAt: string;
    maxOfflineSeconds: number;
    lastOfflineReport: OfflineReport | null;
}

export interface GameRunState {
    resources: ResourceState;
    probe: ProbeState;
    currentExpedition: CurrentExpeditionState;
    research: ResearchState;
    resourceRemainders: ResourceAmounts;
}

export interface GameState {
    schemaVersion: number;
    saveId: string;
    run: GameRunState;
    archive: ArchiveState;
    prestige: PrestigeState;
    quests: GuideQuestState;
    offline: OfflineState;
}

export type GuideQuestFlagKey = keyof GuideQuestFlags;
export type GuideQuestStatus = "completed" | "active" | "locked";

export interface GuideQuestCategoryDefinition {
    id: GuideQuestCategoryId;
    name: string;
    icon: string;
    startId: number;
    endId: number;
}

export type GuideQuestObjective =
    | { type: "flag"; flag: GuideQuestFlagKey }
    | { type: "expeditionStarted" }
    | { type: "resourceLifetime"; resource: keyof ResourceAmounts; target: number }
    | { type: "planetsDiscovered"; target: number }
    | { type: "gradeCompleted"; grade: PlanetGrade; target: number }
    | { type: "specLevel"; specKey: ProbeSpecKey; level: number }
    | { type: "specsAtLevel"; level: number; count: number }
    | { type: "researchCompleted"; nodeId: ResearchNodeId }
    | { type: "researchCompletedCount"; target: number }
    | { type: "artifactTotal"; target: number }
    | { type: "artifactKey"; artifactKey: ArtifactSlot["artifactKey"]; target: number }
    | { type: "artifactSameKind"; target: number }
    | { type: "artifactGrade"; grade: PlanetGrade; target: number }
    | { type: "artifactTypeCount"; target: number }
    | { type: "artifactEachType"; target: number }
    | { type: "dangerSurvived"; target: number }
    | { type: "failedExpeditions"; target: number }
    | { type: "lifeSignal"; signal: "any" | "intelligent"; target: number }
    | { type: "xGradeUnlocked" }
    | { type: "prestigeResetCount"; target: number }
    | { type: "starSystemReached"; starSystemKey: string }
    | { type: "postPrestigePlanetClears"; target: number }
    | { type: "permanentBonusCount"; target: number }
    | { type: "permanentSpeedMultiplier"; target: number }
    | { type: "offlinePartsReward"; target: number }
    | { type: "dailyUpgrades"; target: number }
    | { type: "researchTreeCompletions"; target: number }
    | { type: "multiversePlanetsCompleted"; target: number };

export interface GuideQuestReward {
    explorationData?: number;
    parts?: number;
    permanentBonuses?: Partial<PrestigeBonuses>;
    title?: string;
}

export interface GuideQuestDefinition {
    id: number;
    category: GuideQuestCategoryId;
    title: string;
    objective: GuideQuestObjective;
    reward: GuideQuestReward;
}

export interface GuideQuestProgress {
    current: number;
    target: number;
    percent: number;
    label: string;
}

export interface GuideQuestCompletion {
    questId: number;
    title: string;
    categoryName: string;
    rewardLabel: string;
    completedCount: number;
    totalCount: number;
}

export interface AdvanceSummary {
    planetsDiscovered: number;
    resourcesGained: ResourceAmounts;
    artifactInstanceIds: string[];
}

export interface ActionResult {
    ok: boolean;
    message: string;
}

declare global {
    interface Window {
        resetAstrovaGameForDevelopment?: () => GameState;
    }
}

interface ResearchDefinition {
    id: ResearchNodeId;
    name: string;
    description: string;
    prerequisites: ResearchNodeId[];
    costExplorationData: number;
    maxLevel: number;
    effects: Array<
        | { type: "resourceGainMultiplier"; value: number }
        | { type: "explorationSpeedMultiplier"; value: number }
        | { type: "deepSpaceUnlocked"; value: boolean }
        | { type: "lifeDiscoveryChanceBonus"; value: number }
        | { type: "artifactDiscoveryChanceBonus"; value: number }
    >;
}

interface PartUpgradeDefinition {
    name: string;
    baseCost: number;
    growth: number;
}

interface PrestigeDefinition {
    stage: number;
    starSystemKey: string;
    name: string;
    requiredPlanetClears: number;
    grantedBonuses: Partial<PrestigeBonuses>;
}

interface PlanetGradeDefinition {
    grade: PlanetGrade;
    name: string;
    progressRequired: number;
    distanceTier: number;
    baseShieldRequirement: number;
    completionRewards: ResourceAmounts;
    artifactChance: number;
}

type RandomSource = () => number;

const EMPTY_AMOUNTS: ResourceAmounts = {
    explorationData: 0,
    energy: 0,
    parts: 0
};

export const PART_UPGRADE_DEFINITIONS: Record<ProbeSpecKey, PartUpgradeDefinition> = {
    engine: { name: "Engine", baseCost: 8, growth: 1.62 },
    explorationGear: { name: "Exploration Gear", baseCost: 10, growth: 1.64 },
    shield: { name: "Shield", baseCost: 12, growth: 1.66 },
    antenna: { name: "Antenna", baseCost: 14, growth: 1.68 }
};

export const RESEARCH_NODE_ORDER: ResearchNodeId[] = [
    "basic_exploration",
    "planet_analysis",
    "high_speed_engine",
    "deep_space_exploration",
    "life_detection",
    "artifact_scanner",
    "spectral_mapping",
    "mineral_survey",
    "adaptive_drilling",
    "thermal_refinery",
    "quantum_harvest",
    "planetary_cartography",
    "stellar_logistics",
    "matter_reclaimer",
    "cosmic_extraction",
    "terraforming_index",
    "ion_thrusters",
    "trajectory_ai",
    "gravity_slingshot",
    "warp_calibration",
    "subspace_sails",
    "tachyon_drive",
    "relativistic_navigation",
    "zero_point_engine",
    "event_horizon_drive",
    "lightfold_engine",
    "hazard_prediction",
    "void_signal_array",
    "bio_signature_archive",
    "ancient_translation",
    "xeno_ecology",
    "relic_resonator",
    "deep_space_beacon",
    "multiverse_sensor",
    "forbidden_atlas",
    "origin_signal",
    "biosphere_simulation",
    "orbital_foundry",
    "dark_matter_sieve",
    "stellar_compression",
    "nano_probe_swarm",
    "antimatter_logistics",
    "proto_world_engine",
    "genesis_harvest",
    "photon_lattice",
    "inertial_nullifier",
    "wormhole_geometry",
    "causality_buffer",
    "quantum_wake_drive",
    "horizon_skimming",
    "singularity_pilot",
    "eternity_engine",
    "sentient_signal_model",
    "relic_memory_core",
    "living_world_index",
    "mythic_archive",
    "first_contact_protocol",
    "precursor_gateway",
    "cosmic_language",
    "omega_observatory"
];

export const RESEARCH_DEFINITIONS: Record<ResearchNodeId, ResearchDefinition> = {
    basic_exploration: {
        id: "basic_exploration",
        name: "Basic Exploration",
        description: "Unlocks the first research branches.",
        prerequisites: [],
        costExplorationData: 100,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.05 }]
    },
    planet_analysis: {
        id: "planet_analysis",
        name: "Planet Analysis",
        description: "Resource gain +20%.",
        prerequisites: ["basic_exploration"],
        costExplorationData: 250,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.2 }]
    },
    high_speed_engine: {
        id: "high_speed_engine",
        name: "High Speed Engine",
        description: "Exploration speed +30%.",
        prerequisites: ["basic_exploration"],
        costExplorationData: 320,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.3 }]
    },
    deep_space_exploration: {
        id: "deep_space_exploration",
        name: "Deep Space Exploration",
        description: "Unlocks distant planet rolls.",
        prerequisites: ["basic_exploration"],
        costExplorationData: 520,
        maxLevel: 1,
        effects: [{ type: "deepSpaceUnlocked", value: true }]
    },
    life_detection: {
        id: "life_detection",
        name: "Life Detection",
        description: "Life signal discovery chance +50%.",
        prerequisites: ["deep_space_exploration"],
        costExplorationData: 820,
        maxLevel: 1,
        effects: [{ type: "lifeDiscoveryChanceBonus", value: 0.5 }]
    },
    artifact_scanner: {
        id: "artifact_scanner",
        name: "Artifact Scanner",
        description: "Artifact discovery chance +40%.",
        prerequisites: ["deep_space_exploration"],
        costExplorationData: 900,
        maxLevel: 1,
        effects: [{ type: "artifactDiscoveryChanceBonus", value: 0.4 }]
    },
    spectral_mapping: {
        id: "spectral_mapping",
        name: "스펙트럼 지도화",
        description: "자원 획득량 +8%",
        prerequisites: ["planet_analysis"],
        costExplorationData: 1_200,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.08 }]
    },
    mineral_survey: {
        id: "mineral_survey",
        name: "광물 신호 조사",
        description: "자원 획득량 +10%",
        prerequisites: ["spectral_mapping"],
        costExplorationData: 2_400,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.1 }]
    },
    adaptive_drilling: {
        id: "adaptive_drilling",
        name: "적응형 채굴 드릴",
        description: "자원 획득량 +12%",
        prerequisites: ["mineral_survey"],
        costExplorationData: 4_800,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.12 }]
    },
    thermal_refinery: {
        id: "thermal_refinery",
        name: "열핵 정제소",
        description: "자원 획득량 +14%",
        prerequisites: ["adaptive_drilling"],
        costExplorationData: 9_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.14 }]
    },
    quantum_harvest: {
        id: "quantum_harvest",
        name: "양자 수확 프로토콜",
        description: "자원 획득량 +16%",
        prerequisites: ["thermal_refinery"],
        costExplorationData: 17_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.16 }]
    },
    planetary_cartography: {
        id: "planetary_cartography",
        name: "행성권 지도 제작",
        description: "자원 획득량 +18%",
        prerequisites: ["quantum_harvest"],
        costExplorationData: 32_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.18 }]
    },
    stellar_logistics: {
        id: "stellar_logistics",
        name: "성간 보급 경로",
        description: "자원 획득량 +20%",
        prerequisites: ["planetary_cartography"],
        costExplorationData: 60_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.2 }]
    },
    matter_reclaimer: {
        id: "matter_reclaimer",
        name: "물질 회수 장치",
        description: "자원 획득량 +22%",
        prerequisites: ["stellar_logistics"],
        costExplorationData: 115_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.22 }]
    },
    cosmic_extraction: {
        id: "cosmic_extraction",
        name: "우주 광맥 추출",
        description: "자원 획득량 +25%",
        prerequisites: ["matter_reclaimer"],
        costExplorationData: 220_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.25 }]
    },
    terraforming_index: {
        id: "terraforming_index",
        name: "테라포밍 색인",
        description: "자원 획득량 +28%",
        prerequisites: ["cosmic_extraction"],
        costExplorationData: 420_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.28 }]
    },
    ion_thrusters: {
        id: "ion_thrusters",
        name: "이온 추진기",
        description: "탐사 속도 +8%",
        prerequisites: ["high_speed_engine"],
        costExplorationData: 1_400,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.08 }]
    },
    trajectory_ai: {
        id: "trajectory_ai",
        name: "항로 계산 AI",
        description: "탐사 속도 +10%",
        prerequisites: ["ion_thrusters"],
        costExplorationData: 2_800,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.1 }]
    },
    gravity_slingshot: {
        id: "gravity_slingshot",
        name: "중력 슬링샷",
        description: "탐사 속도 +12%",
        prerequisites: ["trajectory_ai"],
        costExplorationData: 5_400,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.12 }]
    },
    warp_calibration: {
        id: "warp_calibration",
        name: "워프 보정",
        description: "탐사 속도 +14%",
        prerequisites: ["gravity_slingshot"],
        costExplorationData: 10_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.14 }]
    },
    subspace_sails: {
        id: "subspace_sails",
        name: "아공간 돛",
        description: "탐사 속도 +16%",
        prerequisites: ["warp_calibration"],
        costExplorationData: 20_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.16 }]
    },
    tachyon_drive: {
        id: "tachyon_drive",
        name: "타키온 드라이브",
        description: "탐사 속도 +18%",
        prerequisites: ["subspace_sails"],
        costExplorationData: 38_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.18 }]
    },
    relativistic_navigation: {
        id: "relativistic_navigation",
        name: "상대론 항법",
        description: "탐사 속도 +20%",
        prerequisites: ["tachyon_drive"],
        costExplorationData: 72_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.2 }]
    },
    zero_point_engine: {
        id: "zero_point_engine",
        name: "영점 엔진",
        description: "탐사 속도 +23%",
        prerequisites: ["relativistic_navigation"],
        costExplorationData: 135_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.23 }]
    },
    event_horizon_drive: {
        id: "event_horizon_drive",
        name: "사건지평선 구동",
        description: "탐사 속도 +26%",
        prerequisites: ["zero_point_engine"],
        costExplorationData: 255_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.26 }]
    },
    lightfold_engine: {
        id: "lightfold_engine",
        name: "광절첩 엔진",
        description: "탐사 속도 +30%",
        prerequisites: ["event_horizon_drive"],
        costExplorationData: 480_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.3 }]
    },
    hazard_prediction: {
        id: "hazard_prediction",
        name: "위험 예측 배열",
        description: "생명체 발견 확률 +6%",
        prerequisites: ["deep_space_exploration"],
        costExplorationData: 1_600,
        maxLevel: 1,
        effects: [{ type: "lifeDiscoveryChanceBonus", value: 0.06 }]
    },
    void_signal_array: {
        id: "void_signal_array",
        name: "공허 신호 배열",
        description: "유물 발견 확률 +7%",
        prerequisites: ["artifact_scanner"],
        costExplorationData: 3_200,
        maxLevel: 1,
        effects: [{ type: "artifactDiscoveryChanceBonus", value: 0.07 }]
    },
    bio_signature_archive: {
        id: "bio_signature_archive",
        name: "생체 신호 아카이브",
        description: "생명체 발견 확률 +10%",
        prerequisites: ["life_detection"],
        costExplorationData: 6_200,
        maxLevel: 1,
        effects: [{ type: "lifeDiscoveryChanceBonus", value: 0.1 }]
    },
    ancient_translation: {
        id: "ancient_translation",
        name: "고대어 번역기",
        description: "유물 발견 확률 +9%",
        prerequisites: ["void_signal_array"],
        costExplorationData: 12_000,
        maxLevel: 1,
        effects: [{ type: "artifactDiscoveryChanceBonus", value: 0.09 }]
    },
    xeno_ecology: {
        id: "xeno_ecology",
        name: "외계 생태학",
        description: "생명체 발견 확률 +12%",
        prerequisites: ["bio_signature_archive"],
        costExplorationData: 23_000,
        maxLevel: 1,
        effects: [{ type: "lifeDiscoveryChanceBonus", value: 0.12 }]
    },
    relic_resonator: {
        id: "relic_resonator",
        name: "유물 공명기",
        description: "유물 발견 확률 +11%",
        prerequisites: ["ancient_translation"],
        costExplorationData: 44_000,
        maxLevel: 1,
        effects: [{ type: "artifactDiscoveryChanceBonus", value: 0.11 }]
    },
    deep_space_beacon: {
        id: "deep_space_beacon",
        name: "심우주 비콘",
        description: "생명체/유물 발견 확률 +8%",
        prerequisites: ["xeno_ecology", "relic_resonator"],
        costExplorationData: 85_000,
        maxLevel: 1,
        effects: [
            { type: "lifeDiscoveryChanceBonus", value: 0.08 },
            { type: "artifactDiscoveryChanceBonus", value: 0.08 }
        ]
    },
    multiverse_sensor: {
        id: "multiverse_sensor",
        name: "다중우주 센서",
        description: "생명체 +15% / 유물 +13%",
        prerequisites: ["deep_space_beacon"],
        costExplorationData: 160_000,
        maxLevel: 1,
        effects: [
            { type: "lifeDiscoveryChanceBonus", value: 0.15 },
            { type: "artifactDiscoveryChanceBonus", value: 0.13 }
        ]
    },
    forbidden_atlas: {
        id: "forbidden_atlas",
        name: "금지 구역 성도",
        description: "생명체 +10% / 유물 +16%",
        prerequisites: ["multiverse_sensor"],
        costExplorationData: 310_000,
        maxLevel: 1,
        effects: [
            { type: "lifeDiscoveryChanceBonus", value: 0.1 },
            { type: "artifactDiscoveryChanceBonus", value: 0.16 }
        ]
    },
    origin_signal: {
        id: "origin_signal",
        name: "기원 신호 해독",
        description: "속도/자원 +15%, 감지 +10%",
        prerequisites: ["forbidden_atlas"],
        costExplorationData: 600_000,
        maxLevel: 1,
        effects: [
            { type: "explorationSpeedMultiplier", value: 1.15 },
            { type: "resourceGainMultiplier", value: 1.15 },
            { type: "lifeDiscoveryChanceBonus", value: 0.1 },
            { type: "artifactDiscoveryChanceBonus", value: 0.1 }
        ]
    },
    biosphere_simulation: {
        id: "biosphere_simulation",
        name: "생물권 시뮬레이션",
        description: "자원 획득량 +30%",
        prerequisites: ["terraforming_index"],
        costExplorationData: 800_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.3 }]
    },
    orbital_foundry: {
        id: "orbital_foundry",
        name: "궤도 주조소",
        description: "자원 획득량 +32%",
        prerequisites: ["biosphere_simulation"],
        costExplorationData: 1_500_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.32 }]
    },
    dark_matter_sieve: {
        id: "dark_matter_sieve",
        name: "암흑물질 체",
        description: "자원 획득량 +35%",
        prerequisites: ["orbital_foundry"],
        costExplorationData: 2_700_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.35 }]
    },
    stellar_compression: {
        id: "stellar_compression",
        name: "항성 압축 공정",
        description: "자원 획득량 +38%",
        prerequisites: ["dark_matter_sieve"],
        costExplorationData: 4_800_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.38 }]
    },
    nano_probe_swarm: {
        id: "nano_probe_swarm",
        name: "나노 탐사 군집",
        description: "자원 획득량 +42%",
        prerequisites: ["stellar_compression"],
        costExplorationData: 8_500_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.42 }]
    },
    antimatter_logistics: {
        id: "antimatter_logistics",
        name: "반물질 보급망",
        description: "자원 획득량 +46%",
        prerequisites: ["nano_probe_swarm"],
        costExplorationData: 15_000_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.46 }]
    },
    proto_world_engine: {
        id: "proto_world_engine",
        name: "원시 행성 엔진",
        description: "자원 획득량 +50%",
        prerequisites: ["antimatter_logistics"],
        costExplorationData: 26_000_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.5 }]
    },
    genesis_harvest: {
        id: "genesis_harvest",
        name: "제네시스 수확",
        description: "자원 획득량 +55%",
        prerequisites: ["proto_world_engine"],
        costExplorationData: 45_000_000,
        maxLevel: 1,
        effects: [{ type: "resourceGainMultiplier", value: 1.55 }]
    },
    photon_lattice: {
        id: "photon_lattice",
        name: "광자 격자 항로",
        description: "탐사 속도 +32%",
        prerequisites: ["lightfold_engine"],
        costExplorationData: 820_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.32 }]
    },
    inertial_nullifier: {
        id: "inertial_nullifier",
        name: "관성 무효화 장치",
        description: "탐사 속도 +34%",
        prerequisites: ["photon_lattice"],
        costExplorationData: 1_550_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.34 }]
    },
    wormhole_geometry: {
        id: "wormhole_geometry",
        name: "웜홀 기하학",
        description: "탐사 속도 +37%",
        prerequisites: ["inertial_nullifier"],
        costExplorationData: 2_900_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.37 }]
    },
    causality_buffer: {
        id: "causality_buffer",
        name: "인과율 버퍼",
        description: "탐사 속도 +40%",
        prerequisites: ["wormhole_geometry"],
        costExplorationData: 5_200_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.4 }]
    },
    quantum_wake_drive: {
        id: "quantum_wake_drive",
        name: "양자 항적 드라이브",
        description: "탐사 속도 +44%",
        prerequisites: ["causality_buffer"],
        costExplorationData: 9_200_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.44 }]
    },
    horizon_skimming: {
        id: "horizon_skimming",
        name: "지평선 활공",
        description: "탐사 속도 +48%",
        prerequisites: ["quantum_wake_drive"],
        costExplorationData: 16_000_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.48 }]
    },
    singularity_pilot: {
        id: "singularity_pilot",
        name: "특이점 파일럿",
        description: "탐사 속도 +52%",
        prerequisites: ["horizon_skimming"],
        costExplorationData: 28_000_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.52 }]
    },
    eternity_engine: {
        id: "eternity_engine",
        name: "영겁 엔진",
        description: "탐사 속도 +58%",
        prerequisites: ["singularity_pilot"],
        costExplorationData: 50_000_000,
        maxLevel: 1,
        effects: [{ type: "explorationSpeedMultiplier", value: 1.58 }]
    },
    sentient_signal_model: {
        id: "sentient_signal_model",
        name: "지성 신호 모델",
        description: "생명체 발견 확률 +12%",
        prerequisites: ["origin_signal"],
        costExplorationData: 900_000,
        maxLevel: 1,
        effects: [{ type: "lifeDiscoveryChanceBonus", value: 0.12 }]
    },
    relic_memory_core: {
        id: "relic_memory_core",
        name: "유물 기억 코어",
        description: "유물 발견 확률 +12%",
        prerequisites: ["origin_signal"],
        costExplorationData: 1_700_000,
        maxLevel: 1,
        effects: [{ type: "artifactDiscoveryChanceBonus", value: 0.12 }]
    },
    living_world_index: {
        id: "living_world_index",
        name: "생명 행성 색인",
        description: "생명체 발견 확률 +14%",
        prerequisites: ["sentient_signal_model"],
        costExplorationData: 3_200_000,
        maxLevel: 1,
        effects: [{ type: "lifeDiscoveryChanceBonus", value: 0.14 }]
    },
    mythic_archive: {
        id: "mythic_archive",
        name: "신화 기록 보관소",
        description: "유물 발견 확률 +14%",
        prerequisites: ["relic_memory_core"],
        costExplorationData: 5_800_000,
        maxLevel: 1,
        effects: [{ type: "artifactDiscoveryChanceBonus", value: 0.14 }]
    },
    first_contact_protocol: {
        id: "first_contact_protocol",
        name: "첫 접촉 프로토콜",
        description: "생명체 +16% / 유물 +8%",
        prerequisites: ["living_world_index", "mythic_archive"],
        costExplorationData: 10_000_000,
        maxLevel: 1,
        effects: [
            { type: "lifeDiscoveryChanceBonus", value: 0.16 },
            { type: "artifactDiscoveryChanceBonus", value: 0.08 }
        ]
    },
    precursor_gateway: {
        id: "precursor_gateway",
        name: "선구자 관문",
        description: "유물 발견 확률 +18%",
        prerequisites: ["first_contact_protocol"],
        costExplorationData: 18_000_000,
        maxLevel: 1,
        effects: [{ type: "artifactDiscoveryChanceBonus", value: 0.18 }]
    },
    cosmic_language: {
        id: "cosmic_language",
        name: "우주 언어 해석",
        description: "생명체 +18% / 유물 +16%",
        prerequisites: ["precursor_gateway"],
        costExplorationData: 32_000_000,
        maxLevel: 1,
        effects: [
            { type: "lifeDiscoveryChanceBonus", value: 0.18 },
            { type: "artifactDiscoveryChanceBonus", value: 0.16 }
        ]
    },
    omega_observatory: {
        id: "omega_observatory",
        name: "오메가 관측소",
        description: "생명체/유물 발견 확률 +20%",
        prerequisites: ["cosmic_language"],
        costExplorationData: 56_000_000,
        maxLevel: 1,
        effects: [
            { type: "lifeDiscoveryChanceBonus", value: 0.2 },
            { type: "artifactDiscoveryChanceBonus", value: 0.2 }
        ]
    }
};

export const PRESTIGE_DEFINITIONS: PrestigeDefinition[] = [
    {
        stage: 1,
        starSystemKey: "solar_system",
        name: "Solar System",
        requiredPlanetClears: 8,
        grantedBonuses: { explorationSpeedMultiplier: 1.1 }
    },
    {
        stage: 2,
        starSystemKey: "alpha_centauri",
        name: "Alpha Centauri",
        requiredPlanetClears: 20,
        grantedBonuses: { resourceGainMultiplier: 1.2 }
    },
    {
        stage: 3,
        starSystemKey: "orion_nebula",
        name: "Orion Nebula",
        requiredPlanetClears: 50,
        grantedBonuses: { artifactDiscoveryChanceBonus: 0.15 }
    },
    {
        stage: 4,
        starSystemKey: "galactic_core",
        name: "Galactic Core",
        requiredPlanetClears: 100,
        grantedBonuses: { xGradeUnlocked: true }
    },
    {
        stage: 5,
        starSystemKey: "multiverse",
        name: "Multiverse",
        requiredPlanetClears: Number.MAX_SAFE_INTEGER,
        grantedBonuses: { explorationSpeedMultiplier: 1.05, resourceGainMultiplier: 1.05 }
    }
];

export const PLANET_GRADE_DEFINITIONS: Record<PlanetGrade, PlanetGradeDefinition> = {
    F: {
        grade: "F",
        name: "Wasteland Planet",
        progressRequired: 180,
        distanceTier: 1,
        baseShieldRequirement: 0,
        completionRewards: { explorationData: 40, energy: 2, parts: 1 },
        artifactChance: 0.02
    },
    C: {
        grade: "C",
        name: "Common Planet",
        progressRequired: 280,
        distanceTier: 2,
        baseShieldRequirement: 0,
        completionRewards: { explorationData: 85, energy: 3, parts: 2 },
        artifactChance: 0.05
    },
    B: {
        grade: "B",
        name: "Anomaly Planet",
        progressRequired: 420,
        distanceTier: 3,
        baseShieldRequirement: 18,
        completionRewards: { explorationData: 160, energy: 4, parts: 4 },
        artifactChance: 0.1
    },
    A: {
        grade: "A",
        name: "Rare Planet",
        progressRequired: 620,
        distanceTier: 4,
        baseShieldRequirement: 26,
        completionRewards: { explorationData: 280, energy: 6, parts: 7 },
        artifactChance: 0.22
    },
    S: {
        grade: "S",
        name: "Unknown Planet",
        progressRequired: 880,
        distanceTier: 5,
        baseShieldRequirement: 42,
        completionRewards: { explorationData: 560, energy: 10, parts: 14 },
        artifactChance: 1
    },
    X: {
        grade: "X",
        name: "Forbidden Zone",
        progressRequired: 1200,
        distanceTier: 6,
        baseShieldRequirement: 56,
        completionRewards: { explorationData: 900, energy: 14, parts: 22 },
        artifactChance: 0.45
    }
};

export const GUIDE_QUEST_CATEGORIES: GuideQuestCategoryDefinition[] = [
    { id: "exploration_intro", name: "탐사 입문", icon: "📡", startId: 1, endId: 15 },
    { id: "probe_upgrade", name: "탐사선 강화", icon: "⚙️", startId: 16, endId: 30 },
    { id: "research", name: "연구", icon: "🔬", startId: 31, endId: 45 },
    { id: "artifact", name: "유물 발견", icon: "💎", startId: 46, endId: 60 },
    { id: "deep_space", name: "심우주 탐사", icon: "🌌", startId: 61, endId: 75 },
    { id: "prestige", name: "프레스티지", icon: "⭐", startId: 76, endId: 90 },
    { id: "challenge", name: "도전", icon: "🏆", startId: 91, endId: 100 }
];

const introReward = (id: number): GuideQuestReward => ({ explorationData: 30 + id * 18 });
const upgradeReward = (id: number): GuideQuestReward => ({ parts: 10 + (id - 15) * 4 });
const researchReward = (id: number): GuideQuestReward => ({ explorationData: 260 + (id - 30) * 90 });
const artifactReward = (id: number): GuideQuestReward => ({ parts: 70 + (id - 45) * 22 });
const deepSpaceReward = (id: number): GuideQuestReward => ({ explorationData: 2600 + (id - 60) * 850 });
const prestigeReward = (): GuideQuestReward => ({
    permanentBonuses: {
        explorationSpeedMultiplier: 1.03,
        resourceGainMultiplier: 1.015,
        artifactDiscoveryChanceBonus: 0.003
    }
});
const challengeReward = (id: number, title: string): GuideQuestReward => ({
    explorationData: 70_000 + (id - 90) * 18_000,
    parts: 900 + (id - 90) * 180,
    title
});

const q = (
    id: number,
    category: GuideQuestCategoryId,
    title: string,
    objective: GuideQuestObjective,
    reward: GuideQuestReward
): GuideQuestDefinition => ({ id, category, title, objective, reward });

export const GUIDE_QUEST_DEFINITIONS: GuideQuestDefinition[] = [
    q(1, "exploration_intro", "첫 번째 탐사 시작하기", { type: "expeditionStarted" }, introReward(1)),
    q(2, "exploration_intro", "행성 1개 발견하기", { type: "planetsDiscovered", target: 1 }, introReward(2)),
    q(3, "exploration_intro", "탐사 데이터 100 모으기", { type: "resourceLifetime", resource: "explorationData", target: 100 }, introReward(3)),
    q(4, "exploration_intro", "행성 3개 발견하기", { type: "planetsDiscovered", target: 3 }, introReward(4)),
    q(5, "exploration_intro", "F등급 행성 탐사 완료하기", { type: "gradeCompleted", grade: "F", target: 1 }, introReward(5)),
    q(6, "exploration_intro", "탐사 데이터 500 모으기", { type: "resourceLifetime", resource: "explorationData", target: 500 }, introReward(6)),
    q(7, "exploration_intro", "C등급 행성 탐사 완료하기", { type: "gradeCompleted", grade: "C", target: 1 }, introReward(7)),
    q(8, "exploration_intro", "행성 10개 발견하기", { type: "planetsDiscovered", target: 10 }, introReward(8)),
    q(9, "exploration_intro", "에너지 50 모으기", { type: "resourceLifetime", resource: "energy", target: 50 }, introReward(9)),
    q(10, "exploration_intro", "부품 10개 모으기", { type: "resourceLifetime", resource: "parts", target: 10 }, introReward(10)),
    q(11, "exploration_intro", "탐사 데이터 1,000 모으기", { type: "resourceLifetime", resource: "explorationData", target: 1_000 }, introReward(11)),
    q(12, "exploration_intro", "행성 20개 발견하기", { type: "planetsDiscovered", target: 20 }, introReward(12)),
    q(13, "exploration_intro", "B등급 행성 탐사 완료하기", { type: "gradeCompleted", grade: "B", target: 1 }, introReward(13)),
    q(14, "exploration_intro", "탐사 데이터 5,000 모으기", { type: "resourceLifetime", resource: "explorationData", target: 5_000 }, introReward(14)),
    q(15, "exploration_intro", "행성 50개 발견하기", { type: "planetsDiscovered", target: 50 }, introReward(15)),

    q(16, "probe_upgrade", "엔진 첫 강화하기", { type: "specLevel", specKey: "engine", level: 2 }, upgradeReward(16)),
    q(17, "probe_upgrade", "탐사 장비 첫 강화하기", { type: "specLevel", specKey: "explorationGear", level: 2 }, upgradeReward(17)),
    q(18, "probe_upgrade", "방어막 첫 강화하기", { type: "specLevel", specKey: "shield", level: 2 }, upgradeReward(18)),
    q(19, "probe_upgrade", "안테나 첫 강화하기", { type: "specLevel", specKey: "antenna", level: 2 }, upgradeReward(19)),
    q(20, "probe_upgrade", "스펙 1개를 Lv.3으로 올리기", { type: "specsAtLevel", level: 3, count: 1 }, upgradeReward(20)),
    q(21, "probe_upgrade", "부품 50개 모으기", { type: "resourceLifetime", resource: "parts", target: 50 }, upgradeReward(21)),
    q(22, "probe_upgrade", "스펙 2개를 Lv.3으로 올리기", { type: "specsAtLevel", level: 3, count: 2 }, upgradeReward(22)),
    q(23, "probe_upgrade", "엔진을 Lv.5로 올리기", { type: "specLevel", specKey: "engine", level: 5 }, upgradeReward(23)),
    q(24, "probe_upgrade", "방어막을 Lv.5로 올리기", { type: "specLevel", specKey: "shield", level: 5 }, upgradeReward(24)),
    q(25, "probe_upgrade", "모든 스펙 Lv.3 달성", { type: "specsAtLevel", level: 3, count: 4 }, upgradeReward(25)),
    q(26, "probe_upgrade", "부품 200개 모으기", { type: "resourceLifetime", resource: "parts", target: 200 }, upgradeReward(26)),
    q(27, "probe_upgrade", "스펙 1개를 Lv.7로 올리기", { type: "specsAtLevel", level: 7, count: 1 }, upgradeReward(27)),
    q(28, "probe_upgrade", "모든 스펙 Lv.5 달성", { type: "specsAtLevel", level: 5, count: 4 }, upgradeReward(28)),
    q(29, "probe_upgrade", "부품 500개 모으기", { type: "resourceLifetime", resource: "parts", target: 500 }, upgradeReward(29)),
    q(30, "probe_upgrade", "모든 스펙 Lv.7 달성", { type: "specsAtLevel", level: 7, count: 4 }, upgradeReward(30)),

    q(31, "research", "연구 트리 처음 열어보기", { type: "flag", flag: "researchTreeOpened" }, researchReward(31)),
    q(32, "research", "첫 번째 연구 완료하기", { type: "researchCompletedCount", target: 1 }, researchReward(32)),
    q(33, "research", "기초 탐사술 완료하기", { type: "researchCompleted", nodeId: "basic_exploration" }, researchReward(33)),
    q(34, "research", "행성 분석 연구 완료하기", { type: "researchCompleted", nodeId: "planet_analysis" }, researchReward(34)),
    q(35, "research", "고속 엔진 연구 완료하기", { type: "researchCompleted", nodeId: "high_speed_engine" }, researchReward(35)),
    q(36, "research", "탐사 데이터 10,000 모으기", { type: "resourceLifetime", resource: "explorationData", target: 10_000 }, researchReward(36)),
    q(37, "research", "심우주 탐사 연구 완료하기", { type: "researchCompleted", nodeId: "deep_space_exploration" }, researchReward(37)),
    q(38, "research", "생명체 감지 연구 완료하기", { type: "researchCompleted", nodeId: "life_detection" }, researchReward(38)),
    q(39, "research", "유물 스캐너 연구 완료하기", { type: "researchCompleted", nodeId: "artifact_scanner" }, researchReward(39)),
    q(40, "research", "연구 3개 동시 완료 상태 달성", { type: "researchCompletedCount", target: 3 }, researchReward(40)),
    q(41, "research", "탐사 데이터 50,000 모으기", { type: "resourceLifetime", resource: "explorationData", target: 50_000 }, researchReward(41)),
    q(42, "research", "연구 트리 절반 완료하기", { type: "researchCompletedCount", target: Math.ceil(RESEARCH_NODE_ORDER.length / 2) }, researchReward(42)),
    q(43, "research", "탐사 데이터 100,000 모으기", { type: "resourceLifetime", resource: "explorationData", target: 100_000 }, researchReward(43)),
    q(44, "research", "연구 트리 전체 완료하기", { type: "researchCompletedCount", target: RESEARCH_NODE_ORDER.length }, researchReward(44)),
    q(45, "research", "탐사 데이터 500,000 모으기", { type: "resourceLifetime", resource: "explorationData", target: 500_000 }, researchReward(45)),

    q(46, "artifact", "첫 번째 유물 발견하기", { type: "artifactTotal", target: 1 }, artifactReward(46)),
    q(47, "artifact", "유물 보관소 처음 열어보기", { type: "flag", flag: "artifactVaultOpened" }, artifactReward(47)),
    q(48, "artifact", "고대 문명 흔적 발견하기", { type: "artifactKey", artifactKey: "ancient_ruins", target: 1 }, artifactReward(48)),
    q(49, "artifact", "블랙박스 발견하기", { type: "artifactKey", artifactKey: "black_box", target: 1 }, artifactReward(49)),
    q(50, "artifact", "크리스탈 발견하기", { type: "artifactKey", artifactKey: "crystal", target: 1 }, artifactReward(50)),
    q(51, "artifact", "유물 10개 수집하기", { type: "artifactTotal", target: 10 }, artifactReward(51)),
    q(52, "artifact", "같은 유물 3개 수집하기", { type: "artifactSameKind", target: 3 }, artifactReward(52)),
    q(53, "artifact", "A등급 행성에서 유물 발견하기", { type: "artifactGrade", grade: "A", target: 1 }, artifactReward(53)),
    q(54, "artifact", "유물 30개 수집하기", { type: "artifactTotal", target: 30 }, artifactReward(54)),
    q(55, "artifact", "S등급 행성에서 유물 발견하기", { type: "artifactGrade", grade: "S", target: 1 }, artifactReward(55)),
    q(56, "artifact", "유물 50개 수집하기", { type: "artifactTotal", target: 50 }, artifactReward(56)),
    q(57, "artifact", "유물 3종류 모두 발견하기", { type: "artifactTypeCount", target: 3 }, artifactReward(57)),
    q(58, "artifact", "유물 100개 수집하기", { type: "artifactTotal", target: 100 }, artifactReward(58)),
    q(59, "artifact", "S등급 행성 유물 10개 수집하기", { type: "artifactGrade", grade: "S", target: 10 }, artifactReward(59)),
    q(60, "artifact", "유물 200개 수집하기", { type: "artifactTotal", target: 200 }, artifactReward(60)),

    q(61, "deep_space", "A등급 행성 처음 탐사하기", { type: "gradeCompleted", grade: "A", target: 1 }, deepSpaceReward(61)),
    q(62, "deep_space", "A등급 행성 10개 탐사하기", { type: "gradeCompleted", grade: "A", target: 10 }, deepSpaceReward(62)),
    q(63, "deep_space", "S등급 행성 처음 탐사하기", { type: "gradeCompleted", grade: "S", target: 1 }, deepSpaceReward(63)),
    q(64, "deep_space", "위험 행성 생존하기", { type: "dangerSurvived", target: 1 }, deepSpaceReward(64)),
    q(65, "deep_space", "S등급 행성 5개 탐사하기", { type: "gradeCompleted", grade: "S", target: 5 }, deepSpaceReward(65)),
    q(66, "deep_space", "행성 100개 발견하기", { type: "planetsDiscovered", target: 100 }, deepSpaceReward(66)),
    q(67, "deep_space", "탐사 실패 후 귀환하기", { type: "failedExpeditions", target: 1 }, deepSpaceReward(67)),
    q(68, "deep_space", "위험 행성 10회 생존하기", { type: "dangerSurvived", target: 10 }, deepSpaceReward(68)),
    q(69, "deep_space", "S등급 행성 20개 탐사하기", { type: "gradeCompleted", grade: "S", target: 20 }, deepSpaceReward(69)),
    q(70, "deep_space", "행성 200개 발견하기", { type: "planetsDiscovered", target: 200 }, deepSpaceReward(70)),
    q(71, "deep_space", "생명체 발견하기", { type: "lifeSignal", signal: "any", target: 1 }, deepSpaceReward(71)),
    q(72, "deep_space", "지적 생명체 발견하기", { type: "lifeSignal", signal: "intelligent", target: 1 }, deepSpaceReward(72)),
    q(73, "deep_space", "S등급 행성 50개 탐사하기", { type: "gradeCompleted", grade: "S", target: 50 }, deepSpaceReward(73)),
    q(74, "deep_space", "행성 500개 발견하기", { type: "planetsDiscovered", target: 500 }, deepSpaceReward(74)),
    q(75, "deep_space", "X등급 구역 해금하기", { type: "xGradeUnlocked" }, deepSpaceReward(75)),

    q(76, "prestige", "프레스티지 탭 처음 열어보기", { type: "flag", flag: "prestigeTabOpened" }, prestigeReward()),
    q(77, "prestige", "프레스티지 조건 확인하기", { type: "flag", flag: "prestigeConditionChecked" }, prestigeReward()),
    q(78, "prestige", "첫 번째 프레스티지 완료하기", { type: "prestigeResetCount", target: 1 }, prestigeReward()),
    q(79, "prestige", "알파 센타우리 진입하기", { type: "starSystemReached", starSystemKey: "alpha_centauri" }, prestigeReward()),
    q(80, "prestige", "프레스티지 후 행성 10개 발견하기", { type: "postPrestigePlanetClears", target: 10 }, prestigeReward()),
    q(81, "prestige", "영구 보너스 처음 획득하기", { type: "permanentBonusCount", target: 1 }, prestigeReward()),
    q(82, "prestige", "두 번째 프레스티지 완료하기", { type: "prestigeResetCount", target: 2 }, prestigeReward()),
    q(83, "prestige", "오리온 성운 진입하기", { type: "starSystemReached", starSystemKey: "orion_nebula" }, prestigeReward()),
    q(84, "prestige", "영구 보너스 3개 획득하기", { type: "permanentBonusCount", target: 3 }, prestigeReward()),
    q(85, "prestige", "세 번째 프레스티지 완료하기", { type: "prestigeResetCount", target: 3 }, prestigeReward()),
    q(86, "prestige", "은하 중심부 진입하기", { type: "starSystemReached", starSystemKey: "galactic_core" }, prestigeReward()),
    q(87, "prestige", "영구 보너스 탐사 속도 x1.5 달성하기", { type: "permanentSpeedMultiplier", target: 1.5 }, prestigeReward()),
    q(88, "prestige", "네 번째 프레스티지 완료하기", { type: "prestigeResetCount", target: 4 }, prestigeReward()),
    q(89, "prestige", "다중우주 진입하기", { type: "starSystemReached", starSystemKey: "multiverse" }, prestigeReward()),
    q(90, "prestige", "영구 보너스 전체 획득하기", { type: "permanentBonusCount", target: 4 }, prestigeReward()),

    q(91, "challenge", "24시간 오프라인 후 복귀하기", { type: "flag", flag: "offlineReturn24h" }, challengeReward(91, "장기 항해자")),
    q(92, "challenge", "오프라인 보상으로 부품 100개 한 번에 받기", { type: "offlinePartsReward", target: 100 }, challengeReward(92, "무인 채굴자")),
    q(93, "challenge", "하루 만에 스펙 5회 강화하기", { type: "dailyUpgrades", target: 5 }, challengeReward(93, "정비 마스터")),
    q(94, "challenge", "S등급 행성 100개 탐사하기", { type: "gradeCompleted", grade: "S", target: 100 }, challengeReward(94, "심연 관측자")),
    q(95, "challenge", "유물 전 종류 각 10개씩 수집하기", { type: "artifactEachType", target: 10 }, challengeReward(95, "유물 큐레이터")),
    q(96, "challenge", "연구 트리 2회 완성하기 (프레스티지 후)", { type: "researchTreeCompletions", target: 2 }, challengeReward(96, "반복 연구자")),
    q(97, "challenge", "탐사 데이터 1,000,000 모으기", { type: "resourceLifetime", resource: "explorationData", target: 1_000_000 }, challengeReward(97, "데이터 성좌")),
    q(98, "challenge", "행성 1,000개 발견하기", { type: "planetsDiscovered", target: 1_000 }, challengeReward(98, "천체 지도 제작자")),
    q(99, "challenge", "X등급 행성 탐사 완료하기", { type: "gradeCompleted", grade: "X", target: 1 }, challengeReward(99, "금지 구역 생환자")),
    q(100, "challenge", "다중우주 탐사 완료하기", { type: "multiversePlanetsCompleted", target: 1 }, challengeReward(100, "다중우주 항해자"))
];

export function createInitialGameState(now: Date = new Date()): GameState {
    const nowIso = now.toISOString();
    const state: GameState = {
        schemaVersion: 1,
        saveId: "local-player-1",
        run: createInitialRunState(nowIso),
        archive: createInitialArchive(),
        prestige: createInitialPrestige(),
        quests: createInitialGuideQuestState(),
        offline: {
            lastAccessedAt: nowIso,
            lastSavedAt: nowIso,
            maxOfflineSeconds: MAX_OFFLINE_SECONDS,
            lastOfflineReport: null
        }
    };

    refreshResearchAvailability(state, nowIso);
    recalculateEffectiveStats(state);
    startNextExpedition(state, now);
    return state;
}

export function loadGameState(now: Date = new Date()): GameState {
    if (typeof localStorage === "undefined") {
        return createInitialGameState(now);
    }

    try {
        const raw = localStorage.getItem(ASTROVA_STORAGE_KEY);
        if (!raw) {
            return createInitialGameState(now);
        }

        const parsed = JSON.parse(raw) as Partial<GameState>;
        return normalizeGameState(parsed, now);
    } catch {
        return createInitialGameState(now);
    }
}

export function saveGameState(state: GameState, now: Date = new Date()): void {
    if (typeof localStorage === "undefined") {
        return;
    }

    const nowIso = now.toISOString();
    state.offline.lastAccessedAt = nowIso;
    state.offline.lastSavedAt = nowIso;
    try {
        localStorage.setItem(ASTROVA_STORAGE_KEY, JSON.stringify(state));
    } catch {
        // Storage quota failures should not stop the 50ms game loop.
    }
}

export function resetStoredGameState(now: Date = new Date()): GameState {
    return resetAllGameDataForDevelopment(now);
}

export function resetAllGameDataForDevelopment(now: Date = new Date()): GameState {
    if (typeof localStorage !== "undefined") {
        localStorage.removeItem(ASTROVA_STORAGE_KEY);
    }

    const state = createInitialGameState(now);
    saveGameState(state, now);
    return state;
}

export function applyOfflineProgress(state: GameState, now: Date = new Date()): OfflineReport {
    const nowMs = now.getTime();
    const fromMs = parseTime(state.offline.lastAccessedAt, nowMs);
    const expeditionTickMs = parseTime(state.run.currentExpedition.lastTickAt, fromMs);
    const rawElapsedSeconds = Math.max(0, Math.floor((nowMs - fromMs) / 1000));
    const cappedElapsedSeconds = Math.min(rawElapsedSeconds, MAX_OFFLINE_SECONDS);
    const cappedEndMs = fromMs + cappedElapsedSeconds * 1000;
    const rewardStartMs = Math.max(fromMs, expeditionTickMs);
    const rewardElapsedMs = Math.max(0, Math.min(nowMs, cappedEndMs) - rewardStartMs);
    const summary = advanceGameByElapsedMs(state, rewardElapsedMs, new Date(rewardStartMs));
    const report: OfflineReport = {
        from: new Date(fromMs).toISOString(),
        to: now.toISOString(),
        elapsedSeconds: cappedElapsedSeconds,
        planetsDiscovered: summary.planetsDiscovered,
        resourcesGained: summary.resourcesGained,
        artifactInstanceIds: summary.artifactInstanceIds
    };

    state.offline.lastAccessedAt = now.toISOString();
    state.offline.lastSavedAt = now.toISOString();
    state.offline.maxOfflineSeconds = MAX_OFFLINE_SECONDS;
    state.offline.lastOfflineReport = report;

    const questState = ensureGuideQuestState(state);
    if (report.elapsedSeconds >= MAX_OFFLINE_SECONDS) {
        questState.flags.offlineReturn24h = true;
    }
    questState.stats.maxOfflinePartsReward = Math.max(questState.stats.maxOfflinePartsReward, report.resourcesGained.parts);

    return report;
}

export function advanceGameByElapsedMs(
    state: GameState,
    elapsedMs: number,
    startAt: Date = new Date()
): AdvanceSummary {
    const summary = createEmptySummary();
    if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) {
        return summary;
    }

    let cursorMs = startAt.getTime();
    let remainingMs = elapsedMs;
    let guard = 0;

    while (remainingMs > 0 && guard < 10000) {
        guard++;
        ensureActiveExpedition(state, new Date(cursorMs));
        const expedition = state.run.currentExpedition;
        if (!expedition.targetPlanet) {
            break;
        }

        const progressRatePerMs = getProgressRatePerMs(state);
        const remainingProgress = Math.max(0, expedition.progress.required - expedition.progress.current);
        const msToComplete = progressRatePerMs > 0 ? remainingProgress / progressRatePerMs : remainingMs;
        const stepMs = Math.max(1, Math.min(remainingMs, msToComplete));
        const tickRewards = accrueTickResources(state, stepMs);
        mergeResourceAmounts(summary.resourcesGained, tickRewards);

        expedition.progress.current = Math.min(
            expedition.progress.required,
            expedition.progress.current + progressRatePerMs * stepMs
        );
        cursorMs += stepMs;
        remainingMs -= stepMs;
        expedition.lastTickAt = new Date(cursorMs).toISOString();

        if (expedition.progress.current >= expedition.progress.required) {
            const completion = resolveExpeditionCompletion(state, new Date(cursorMs));
            summary.planetsDiscovered += 1;
            mergeResourceAmounts(summary.resourcesGained, completion.resourcesGained);
            summary.artifactInstanceIds.push(...completion.artifactInstanceIds);
            startNextExpedition(state, new Date(cursorMs));
        }
    }

    return summary;
}

export function getPartUpgradeCost(state: GameState, specKey: ProbeSpecKey): number {
    const definition = PART_UPGRADE_DEFINITIONS[specKey];
    const level = state.run.probe.specs[specKey].level;
    return Math.ceil(definition.baseCost * Math.pow(definition.growth, level));
}

export function upgradeProbePart(state: GameState, specKey: ProbeSpecKey): ActionResult {
    const cost = getPartUpgradeCost(state, specKey);
    const parts = state.run.resources.parts;
    if (parts.amount < cost) {
        return {
            ok: false,
            message: `Not enough parts. Required ${cost}, owned ${parts.amount}.`
        };
    }

    parts.amount -= cost;
    parts.lifetimeSpent += cost;
    const spec = state.run.probe.specs[specKey];
    spec.level += 1;
    spec.spentParts += cost;
    recordGuideQuestUpgrade(state);
    recalculateEffectiveStats(state);
    updateExpectedCompletion(state);
    return {
        ok: true,
        message: `${PART_UPGRADE_DEFINITIONS[specKey].name} upgraded to Lv.${spec.level}.`
    };
}

export function completeResearch(state: GameState, nodeId: ResearchNodeId, now: Date = new Date()): ActionResult {
    const definition = RESEARCH_DEFINITIONS[nodeId];
    const node = state.run.research.nodes[nodeId];
    const missingPrerequisite = definition.prerequisites.find((prerequisite) => {
        return state.run.research.nodes[prerequisite].status !== "completed";
    });

    if (missingPrerequisite) {
        return {
            ok: false,
            message: `${definition.name} requires ${RESEARCH_DEFINITIONS[missingPrerequisite].name}.`
        };
    }

    if (node.level >= definition.maxLevel || node.status === "completed") {
        return { ok: false, message: `${definition.name} is already completed.` };
    }

    if (state.run.resources.explorationData.amount < definition.costExplorationData) {
        return {
            ok: false,
            message: `Not enough exploration data. Required ${definition.costExplorationData}.`
        };
    }

    state.run.resources.explorationData.amount -= definition.costExplorationData;
    state.run.resources.explorationData.lifetimeSpent += definition.costExplorationData;
    node.level += 1;
    node.spentExplorationData += definition.costExplorationData;
    node.status = node.level >= definition.maxLevel ? "completed" : "available";
    node.completedAt = node.status === "completed" ? now.toISOString() : null;
    if (!node.unlockedAt) {
        node.unlockedAt = now.toISOString();
    }

    state.run.research.activeEffects = calculateResearchEffects(state.run.research);
    refreshResearchAvailability(state, now.toISOString());
    recordGuideQuestResearchTreeCompletion(state);
    recalculateEffectiveStats(state);
    updateExpectedCompletion(state);
    return { ok: true, message: `${definition.name} completed.` };
}

export function canPrestige(state: GameState): boolean {
    const progress = state.prestige.currentStageProgress;
    return progress.clearedPlanetCount >= progress.requiredPlanetClears && progress.sGradeCleared;
}

export function applyPrestige(state: GameState, now: Date = new Date()): ActionResult {
    if (!canPrestige(state)) {
        return {
            ok: false,
            message: "Prestige requires the stage planet clear count and one completed S-grade planet."
        };
    }

    const definition = getPrestigeDefinition(state.prestige.currentStage);
    const nextDefinition = getPrestigeDefinition(state.prestige.currentStage + 1);
    const grantedBonuses = definition.grantedBonuses;
    applyPermanentBonuses(state.prestige.permanentBonuses, grantedBonuses);

    if (!state.prestige.completedStarSystems.includes(definition.starSystemKey)) {
        state.prestige.completedStarSystems.push(definition.starSystemKey);
    }

    state.prestige.history.push({
        stage: definition.stage,
        starSystemKey: definition.starSystemKey,
        prestigedAt: now.toISOString(),
        grantedBonuses
    });
    state.prestige.resetCount += 1;
    state.prestige.currentStage = nextDefinition.stage;
    state.prestige.currentStarSystemKey = nextDefinition.starSystemKey;
    state.prestige.currentStageProgress = {
        requiredPlanetClears: nextDefinition.requiredPlanetClears,
        clearedPlanetCount: 0,
        sGradeCleared: false
    };

    state.run = createInitialRunState(now.toISOString());
    ensureGuideQuestState(state).stats.currentRunResearchTreeCompleted = false;
    refreshResearchAvailability(state, now.toISOString());
    recalculateEffectiveStats(state);
    startNextExpedition(state, now);
    state.offline.lastAccessedAt = now.toISOString();
    state.offline.lastSavedAt = now.toISOString();
    state.offline.lastOfflineReport = null;

    return {
        ok: true,
        message: `${definition.name} prestiged. Permanent bonuses were applied.`
    };
}

export function markGuideQuestFlag(state: GameState, flag: GuideQuestFlagKey): void {
    ensureGuideQuestState(state).flags[flag] = true;
}

export function evaluateGuideQuests(state: GameState): GuideQuestCompletion[] {
    ensureGuideQuestState(state);
    const completions: GuideQuestCompletion[] = [];
    let guard = 0;

    while (guard < GUIDE_QUEST_DEFINITIONS.length) {
        guard++;
        const quest = getActiveGuideQuestDefinition(state);
        if (!quest) {
            break;
        }

        const progress = getGuideQuestProgress(state, quest);
        if (progress.current < progress.target) {
            break;
        }

        const questState = ensureGuideQuestState(state);
        if (!questState.completedQuestIds.includes(quest.id)) {
            questState.completedQuestIds.push(quest.id);
        }

        const rewardLabel = applyGuideQuestReward(state, quest.reward);
        completions.push({
            questId: quest.id,
            title: quest.title,
            categoryName: getGuideQuestCategory(quest.category).name,
            rewardLabel,
            completedCount: getGuideQuestCompletedCount(state),
            totalCount: GUIDE_QUEST_DEFINITIONS.length
        });
    }

    return completions;
}

export function getActiveGuideQuestDefinition(state: GameState): GuideQuestDefinition | null {
    const completed = new Set(ensureGuideQuestState(state).completedQuestIds);
    return GUIDE_QUEST_DEFINITIONS.find((quest) => !completed.has(quest.id)) || null;
}

export function getGuideQuestStatus(state: GameState, quest: GuideQuestDefinition): GuideQuestStatus {
    const active = getActiveGuideQuestDefinition(state);
    const completed = ensureGuideQuestState(state).completedQuestIds.includes(quest.id);
    if (completed) {
        return "completed";
    }

    return active?.id === quest.id ? "active" : "locked";
}

export function getGuideQuestProgress(state: GameState, quest: GuideQuestDefinition): GuideQuestProgress {
    const raw = getGuideQuestObjectiveProgress(state, quest.objective);
    const target = Math.max(1, raw.target);
    const current = Math.max(0, Math.min(raw.current, target));
    return {
        current,
        target,
        percent: formatPercent(current / target),
        label: `${formatCompactValue(current)} / ${formatCompactValue(target)}`
    };
}

export function getGuideQuestCompletedCount(state: GameState): number {
    return ensureGuideQuestState(state).completedQuestIds.length;
}

export function getGuideQuestCategory(categoryId: GuideQuestCategoryId): GuideQuestCategoryDefinition {
    return GUIDE_QUEST_CATEGORIES.find((category) => category.id === categoryId) || GUIDE_QUEST_CATEGORIES[0];
}

export function formatGuideQuestRewardLabel(reward: GuideQuestReward): string {
    const labels: string[] = [];
    if (reward.explorationData) {
        labels.push(`탐사 데이터 +${formatCompactValue(reward.explorationData)}`);
    }

    if (reward.parts) {
        labels.push(`부품 +${formatCompactValue(reward.parts)}`);
    }

    if (reward.permanentBonuses?.explorationSpeedMultiplier) {
        labels.push(`영구 속도 x${reward.permanentBonuses.explorationSpeedMultiplier.toFixed(2)}`);
    }

    if (reward.permanentBonuses?.resourceGainMultiplier) {
        labels.push(`영구 자원 x${reward.permanentBonuses.resourceGainMultiplier.toFixed(3)}`);
    }

    if (reward.permanentBonuses?.artifactDiscoveryChanceBonus) {
        labels.push(`유물 확률 +${Math.round(reward.permanentBonuses.artifactDiscoveryChanceBonus * 1000) / 10}%`);
    }

    if (reward.permanentBonuses?.xGradeUnlocked) {
        labels.push("X등급 개방");
    }

    if (reward.title) {
        labels.push(`칭호 ${reward.title}`);
    }

    return labels.length > 0 ? labels.join(" / ") : "보상 없음";
}

export function getLatestPlanet(state: GameState): DiscoveredPlanetRecord | null {
    const latestId = state.archive.discoveredPlanets.order[state.archive.discoveredPlanets.order.length - 1];
    if (!latestId) {
        return null;
    }

    return state.archive.discoveredPlanets.byId[latestId] ?? null;
}

export function formatPercent(value: number): number {
    return Math.max(0, Math.min(100, Math.round(value * 1000) / 10));
}

export function formatCompactValue(value: number): string {
    const absolute = Math.abs(value);
    if (absolute >= 1_000_000_000) {
        return `${trimCompactDecimal(value / 1_000_000_000)}B`;
    }

    if (absolute >= 1_000_000) {
        return `${trimCompactDecimal(value / 1_000_000)}M`;
    }

    if (absolute >= 1_000) {
        return `${trimCompactDecimal(value / 1_000)}K`;
    }

    if (absolute > 0 && absolute < 1) {
        return value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
    }

    return `${Math.round(value * 10) / 10}`.replace(/\.0$/, "");
}

export function formatOfflineHours(elapsedSeconds: number): string {
    const hours = Math.max(0, elapsedSeconds) / 3600;
    return hours < 1 ? (Math.round(hours * 10) / 10).toString() : formatCompactValue(hours);
}

export function buildOfflineReportLines(report: OfflineReport): string[] {
    return [
        `ASTROVA-1이 ${formatOfflineHours(report.elapsedSeconds)}시간 동안 탐사를 진행했습니다.`,
        `행성 ${formatCompactValue(report.planetsDiscovered)}개 발견`,
        `탐사 데이터 ${formatCompactValue(report.resourcesGained.explorationData)} 수집`,
        `유물 ${formatCompactValue(report.artifactInstanceIds.length)}개 발견`
    ];
}

function createInitialResources(nowIso: string): ResourceState {
    return {
        explorationData: {
            amount: 150,
            lifetimeEarned: 150,
            lifetimeSpent: 0
        },
        energy: {
            current: 100,
            capacity: 100,
            lifetimeEarned: 100,
            lifetimeSpent: 0,
            lastRecoveredAt: nowIso
        },
        parts: {
            amount: 32,
            lifetimeEarned: 32,
            lifetimeSpent: 0
        }
    };
}

function createInitialRunState(nowIso: string): GameRunState {
    return {
        resources: createInitialResources(nowIso),
        probe: createInitialProbe(),
        currentExpedition: createIdleExpedition(nowIso),
        research: createInitialResearch(nowIso),
        resourceRemainders: { ...EMPTY_AMOUNTS }
    };
}

function createInitialProbe(): ProbeState {
    return {
        probeId: "ASTROVA-1",
        specs: {
            engine: { level: 1, spentParts: 0 },
            explorationGear: { level: 1, spentParts: 0 },
            shield: { level: 1, spentParts: 0 },
            antenna: { level: 1, spentParts: 0 }
        },
        cachedEffectiveStats: {
            explorationSpeedMultiplier: 1,
            resourceGainMultiplier: 1,
            survivalPower: 0,
            rareDiscoveryChanceBonus: 0,
            lifeDiscoveryChanceBonus: 0,
            deepSpaceUnlocked: false
        }
    };
}

function createInitialResearch(nowIso: string): ResearchState {
    const nodes = {} as Record<ResearchNodeId, ResearchNodeState>;
    for (const id of RESEARCH_NODE_ORDER) {
        const definition = RESEARCH_DEFINITIONS[id];
        nodes[id] = {
            status: definition.prerequisites.length === 0 ? "available" : "locked",
            level: 0,
            maxLevel: definition.maxLevel,
            spentExplorationData: 0,
            unlockedAt: definition.prerequisites.length === 0 ? nowIso : null,
            completedAt: null
        };
    }

    return {
        treeVersion: 1,
        nodes,
        activeEffects: createEmptyResearchEffects()
    };
}

function createInitialArchive(): ArchiveState {
    return {
        discoveredPlanets: {
            byId: {},
            order: []
        },
        artifactCollection: {
            byInstanceId: {},
            summaryByArtifactKey: {}
        }
    };
}

function createInitialPrestige(): PrestigeState {
    const firstStage = getPrestigeDefinition(1);
    return {
        currentStage: firstStage.stage,
        currentStarSystemKey: firstStage.starSystemKey,
        resetCount: 0,
        completedStarSystems: [],
        currentStageProgress: {
            requiredPlanetClears: firstStage.requiredPlanetClears,
            clearedPlanetCount: 0,
            sGradeCleared: false
        },
        permanentBonuses: {
            explorationSpeedMultiplier: 1,
            resourceGainMultiplier: 1,
            artifactDiscoveryChanceBonus: 0,
            xGradeUnlocked: false
        },
        history: []
    };
}

function createInitialGuideQuestState(): GuideQuestState {
    return {
        completedQuestIds: [],
        completedTitles: [],
        flags: {
            expeditionStarted: false,
            researchTreeOpened: false,
            artifactVaultOpened: false,
            prestigeTabOpened: false,
            prestigeConditionChecked: false,
            offlineReturn24h: false
        },
        stats: {
            dailyUpgradeDate: null,
            dailyUpgradeCount: 0,
            maxDailyUpgradeCount: 0,
            maxOfflinePartsReward: 0,
            researchTreesCompleted: 0,
            currentRunResearchTreeCompleted: false,
            multiversePlanetsCompleted: 0
        }
    };
}

function normalizeGuideQuestState(raw: Partial<GuideQuestState> | undefined): GuideQuestState {
    const fallback = createInitialGuideQuestState();
    const flags: Partial<GuideQuestFlags> = raw?.flags || {};
    const stats: Partial<GuideQuestStats> = raw?.stats || {};
    const knownQuestIds = new Set(GUIDE_QUEST_DEFINITIONS.map((quest) => quest.id));
    const completedQuestIds = Array.isArray(raw?.completedQuestIds)
        ? raw.completedQuestIds.filter((id) => knownQuestIds.has(id))
        : [];

    return {
        completedQuestIds: Array.from(new Set(completedQuestIds)).sort((left, right) => left - right),
        completedTitles: Array.isArray(raw?.completedTitles) ? Array.from(new Set(raw.completedTitles)) : [],
        flags: {
            expeditionStarted: Boolean(flags.expeditionStarted),
            researchTreeOpened: Boolean(flags.researchTreeOpened),
            artifactVaultOpened: Boolean(flags.artifactVaultOpened),
            prestigeTabOpened: Boolean(flags.prestigeTabOpened),
            prestigeConditionChecked: Boolean(flags.prestigeConditionChecked),
            offlineReturn24h: Boolean(flags.offlineReturn24h)
        },
        stats: {
            dailyUpgradeDate: typeof stats.dailyUpgradeDate === "string" ? stats.dailyUpgradeDate : fallback.stats.dailyUpgradeDate,
            dailyUpgradeCount: safeNumber(stats.dailyUpgradeCount),
            maxDailyUpgradeCount: safeNumber(stats.maxDailyUpgradeCount),
            maxOfflinePartsReward: safeNumber(stats.maxOfflinePartsReward),
            researchTreesCompleted: safeNumber(stats.researchTreesCompleted),
            currentRunResearchTreeCompleted: Boolean(stats.currentRunResearchTreeCompleted),
            multiversePlanetsCompleted: safeNumber(stats.multiversePlanetsCompleted)
        }
    };
}

function createIdleExpedition(nowIso: string): CurrentExpeditionState {
    return {
        status: "idle",
        startedAt: nowIso,
        lastTickAt: nowIso,
        expectedCompletedAt: nowIso,
        progress: {
            current: 0,
            required: PLANET_GRADE_DEFINITIONS.F.progressRequired
        },
        targetPlanet: null,
        pendingRewards: {
            explorationData: 0,
            energy: 0,
            parts: 0,
            artifactInstanceIds: []
        },
        failureState: null
    };
}

function normalizeGameState(raw: Partial<GameState>, now: Date): GameState {
    const fallback = createInitialGameState(now);
    const state = raw as GameState;
    if (!state.run || !state.archive || !state.prestige || !state.offline) {
        return fallback;
    }

    state.schemaVersion = 1;
    state.saveId = state.saveId || fallback.saveId;
    state.run.resources = state.run.resources || fallback.run.resources;
    state.run.probe = state.run.probe || fallback.run.probe;
    state.run.currentExpedition = state.run.currentExpedition || fallback.run.currentExpedition;
    state.run.research = state.run.research || fallback.run.research;
    state.run.resourceRemainders = state.run.resourceRemainders || { ...EMPTY_AMOUNTS };
    state.archive.discoveredPlanets = state.archive.discoveredPlanets || fallback.archive.discoveredPlanets;
    state.archive.artifactCollection = state.archive.artifactCollection || fallback.archive.artifactCollection;
    state.prestige.permanentBonuses = state.prestige.permanentBonuses || fallback.prestige.permanentBonuses;
    state.prestige.currentStageProgress = state.prestige.currentStageProgress || fallback.prestige.currentStageProgress;
    state.prestige.completedStarSystems = state.prestige.completedStarSystems || [];
    state.prestige.history = state.prestige.history || [];
    state.quests = normalizeGuideQuestState(state.quests);
    state.offline.maxOfflineSeconds = MAX_OFFLINE_SECONDS;
    state.offline.lastAccessedAt = state.offline.lastAccessedAt || now.toISOString();
    state.offline.lastSavedAt = state.offline.lastSavedAt || now.toISOString();
    state.offline.lastOfflineReport = state.offline.lastOfflineReport || null;

    for (const id of RESEARCH_NODE_ORDER) {
        if (!state.run.research.nodes[id]) {
            state.run.research.nodes[id] = fallback.run.research.nodes[id];
        }
    }

    state.run.research.activeEffects = calculateResearchEffects(state.run.research);
    refreshResearchAvailability(state, now.toISOString());
    recalculateEffectiveStats(state);
    ensureActiveExpedition(state, now);
    return state;
}

function ensureActiveExpedition(state: GameState, now: Date): void {
    if (state.run.currentExpedition.status === "exploring" && state.run.currentExpedition.targetPlanet) {
        return;
    }

    startNextExpedition(state, now);
}

function startNextExpedition(state: GameState, now: Date): void {
    const targetPlanet = generatePlanet(state, now);
    const required = PLANET_GRADE_DEFINITIONS[targetPlanet.grade].progressRequired;
    const nowIso = now.toISOString();
    state.run.currentExpedition = {
        status: "exploring",
        startedAt: nowIso,
        lastTickAt: nowIso,
        expectedCompletedAt: calculateExpectedCompletedAt(state, required, 0, now),
        progress: {
            current: 0,
            required
        },
        targetPlanet,
        pendingRewards: {
            explorationData: 0,
            energy: 0,
            parts: 0,
            artifactInstanceIds: []
        },
        failureState: null
    };
    markGuideQuestFlag(state, "expeditionStarted");
}

function generatePlanet(state: GameState, now: Date): TargetPlanet {
    const seed = createSeed(now);
    const rng = createSeededRandom(seed);
    const grade = pickWeighted(rng, getGradeWeights(state));
    const gradeDefinition = PLANET_GRADE_DEFINITIONS[grade];
    const planetType = pickOne<PlanetType>(rng, PLANET_TYPES);
    const stats = state.run.probe.cachedEffectiveStats;
    const lifeSignal = rollLifeSignal(rng, stats.lifeDiscoveryChanceBonus);
    const hazards = createHazards(rng, gradeDefinition);
    const artifactSlots = createArtifactSlots(rng, gradeDefinition, stats.rareDiscoveryChanceBonus);

    return {
        planetId: `planet-${formatDateKey(now)}-${Math.floor(rng() * 100000).toString(36)}`,
        seed,
        name: `${pickOne(rng, ["Vesper", "Aster", "Nox", "Lumen", "Oria", "Kepler"])}-${Math.floor(rng() * 90) + 10}`,
        grade,
        type: planetType,
        distanceTier: gradeDefinition.distanceTier,
        lifeSignal,
        hazards,
        artifactSlots
    };
}

function resolveExpeditionCompletion(state: GameState, completedAt: Date): AdvanceSummary {
    const expedition = state.run.currentExpedition;
    const targetPlanet = expedition.targetPlanet;
    const summary = createEmptySummary();
    if (!targetPlanet) {
        return summary;
    }

    const failure = getFailureState(state, targetPlanet, completedAt);
    if (failure) {
        const lostParts = Math.min(state.run.resources.parts.amount, failure.lostParts);
        state.run.resources.parts.amount -= lostParts;
        state.run.resources.parts.lifetimeSpent += lostParts;
        expedition.status = "failed";
        expedition.failureState = { ...failure, lostParts };
        archivePlanet(state, targetPlanet, completedAt, "failed", { ...EMPTY_AMOUNTS }, []);
        return summary;
    }

    const rewards = calculateCompletionRewards(state, targetPlanet);
    const appliedRewards = addResources(state, rewards);
    const artifactIds = collectArtifacts(state, targetPlanet, completedAt);
    targetPlanet.artifactSlots.forEach((slot) => {
        slot.discovered = artifactIds.length > 0;
    });

    expedition.status = "completed";
    expedition.pendingRewards = {
        ...appliedRewards,
        artifactInstanceIds: artifactIds
    };
    archivePlanet(state, targetPlanet, completedAt, "completed", appliedRewards, artifactIds);
    state.prestige.currentStageProgress.clearedPlanetCount += 1;
    if (targetPlanet.grade === "S") {
        state.prestige.currentStageProgress.sGradeCleared = true;
    }
    if (state.prestige.currentStarSystemKey === "multiverse") {
        ensureGuideQuestState(state).stats.multiversePlanetsCompleted += 1;
    }

    mergeResourceAmounts(summary.resourcesGained, appliedRewards);
    summary.artifactInstanceIds.push(...artifactIds);
    return summary;
}

function getFailureState(state: GameState, planet: TargetPlanet, failedAt: Date): FailureState | null {
    const requiredShieldPower = planet.hazards.reduce((max, hazard) => {
        return Math.max(max, hazard.requiredShieldPower);
    }, PLANET_GRADE_DEFINITIONS[planet.grade].baseShieldRequirement);
    const survivalPower = state.run.probe.cachedEffectiveStats.survivalPower;
    if (requiredShieldPower <= 0 || survivalPower >= requiredShieldPower) {
        return null;
    }

    const shortage = requiredShieldPower - survivalPower;
    const currentParts = state.run.resources.parts.amount;
    const safeMaximumLoss = Math.max(0, currentParts - 1);
    const calculatedLoss = Math.max(1, Math.ceil(shortage * 0.35 + PLANET_GRADE_DEFINITIONS[planet.grade].distanceTier));
    return {
        reason: "insufficient_shield",
        requiredShieldPower,
        survivalPower,
        lostParts: Math.min(safeMaximumLoss, calculatedLoss),
        failedAt: failedAt.toISOString()
    };
}

function accrueTickResources(state: GameState, elapsedMs: number): ResourceAmounts {
    const elapsedSeconds = elapsedMs / 1000;
    const stats = state.run.probe.cachedEffectiveStats;
    const generated: ResourceAmounts = {
        explorationData: elapsedSeconds * 4.5 * stats.resourceGainMultiplier,
        parts: elapsedSeconds * 0.04 * stats.resourceGainMultiplier,
        energy: elapsedSeconds * 0.035
    };
    const gained = { ...EMPTY_AMOUNTS };

    state.run.resourceRemainders.explorationData += generated.explorationData;
    state.run.resourceRemainders.parts += generated.parts;
    state.run.resourceRemainders.energy += generated.energy;

    const explorationData = Math.floor(state.run.resourceRemainders.explorationData);
    if (explorationData > 0) {
        state.run.resourceRemainders.explorationData -= explorationData;
        gained.explorationData = explorationData;
    }

    const parts = Math.floor(state.run.resourceRemainders.parts);
    if (parts > 0) {
        state.run.resourceRemainders.parts -= parts;
        gained.parts = parts;
    }

    const energy = Math.floor(state.run.resourceRemainders.energy);
    if (energy > 0) {
        state.run.resourceRemainders.energy -= energy;
        gained.energy = energy;
    }

    return addResources(state, gained);
}

function calculateCompletionRewards(state: GameState, planet: TargetPlanet): ResourceAmounts {
    const base = PLANET_GRADE_DEFINITIONS[planet.grade].completionRewards;
    const multiplier = state.run.probe.cachedEffectiveStats.resourceGainMultiplier;
    return {
        explorationData: Math.floor(base.explorationData * multiplier),
        energy: Math.floor(base.energy),
        parts: Math.floor(base.parts * multiplier)
    };
}

function collectArtifacts(state: GameState, planet: TargetPlanet, foundAt: Date): string[] {
    const artifactIds: string[] = [];
    for (const slot of planet.artifactSlots) {
        const artifactInstanceId = `artifact-${formatDateKey(foundAt)}-${artifactIds.length + 1}-${planet.planetId.slice(-4)}`;
        const record: ArtifactRecord = {
            artifactInstanceId,
            artifactKey: slot.artifactKey,
            rarity: slot.rarity,
            sourcePlanetId: planet.planetId,
            foundAt: foundAt.toISOString(),
            prestigeStageFound: state.prestige.currentStage
        };
        state.archive.artifactCollection.byInstanceId[artifactInstanceId] = record;
        updateArtifactSummary(state.archive, record);
        artifactIds.push(artifactInstanceId);
    }

    return artifactIds;
}

function archivePlanet(
    state: GameState,
    planet: TargetPlanet,
    completedAt: Date,
    outcome: DiscoveredPlanetRecord["outcome"],
    earnedResources: ResourceAmounts,
    artifactInstanceIds: string[]
): void {
    const record: DiscoveredPlanetRecord = {
        planetId: planet.planetId,
        seed: planet.seed,
        name: planet.name,
        grade: planet.grade,
        type: planet.type,
        lifeSignal: planet.lifeSignal,
        hazards: planet.hazards.map((hazard) => hazard.type),
        discoveredAt: completedAt.toISOString(),
        completedAt: outcome === "completed" ? completedAt.toISOString() : null,
        outcome,
        earnedResources,
        artifactInstanceIds
    };

    state.archive.discoveredPlanets.byId[planet.planetId] = record;
    state.archive.discoveredPlanets.order.push(planet.planetId);
}

function updateArtifactSummary(archive: ArchiveState, record: ArtifactRecord): void {
    const existing = archive.artifactCollection.summaryByArtifactKey[record.artifactKey];
    if (!existing) {
        archive.artifactCollection.summaryByArtifactKey[record.artifactKey] = {
            artifactKey: record.artifactKey,
            count: 1,
            firstFoundAt: record.foundAt,
            lastFoundAt: record.foundAt,
            sourcePlanetIds: [record.sourcePlanetId]
        };
        return;
    }

    existing.count += 1;
    existing.lastFoundAt = record.foundAt;
    if (!existing.sourcePlanetIds.includes(record.sourcePlanetId)) {
        existing.sourcePlanetIds.push(record.sourcePlanetId);
    }
}

function ensureGuideQuestState(state: GameState): GuideQuestState {
    state.quests = normalizeGuideQuestState(state.quests);
    return state.quests;
}

function recordGuideQuestUpgrade(state: GameState, now: Date = new Date()): void {
    const questState = ensureGuideQuestState(state);
    const dayKey = now.toISOString().slice(0, 10);
    if (questState.stats.dailyUpgradeDate !== dayKey) {
        questState.stats.dailyUpgradeDate = dayKey;
        questState.stats.dailyUpgradeCount = 0;
    }

    questState.stats.dailyUpgradeCount += 1;
    questState.stats.maxDailyUpgradeCount = Math.max(
        questState.stats.maxDailyUpgradeCount,
        questState.stats.dailyUpgradeCount
    );
}

function recordGuideQuestResearchTreeCompletion(state: GameState): void {
    const questState = ensureGuideQuestState(state);
    if (questState.stats.currentRunResearchTreeCompleted) {
        return;
    }

    const completedCount = getCompletedResearchCount(state);
    if (completedCount < RESEARCH_NODE_ORDER.length) {
        return;
    }

    questState.stats.currentRunResearchTreeCompleted = true;
    questState.stats.researchTreesCompleted += 1;
}

function applyGuideQuestReward(state: GameState, reward: GuideQuestReward): string {
    if (reward.explorationData || reward.parts) {
        addResources(state, {
            explorationData: reward.explorationData || 0,
            energy: 0,
            parts: reward.parts || 0
        });
    }

    if (reward.permanentBonuses) {
        applyPermanentBonuses(state.prestige.permanentBonuses, reward.permanentBonuses);
        recalculateEffectiveStats(state);
        updateExpectedCompletion(state);
    }

    if (reward.title) {
        const titles = ensureGuideQuestState(state).completedTitles;
        if (!titles.includes(reward.title)) {
            titles.push(reward.title);
        }
    }

    return formatGuideQuestRewardLabel(reward);
}

function getGuideQuestObjectiveProgress(
    state: GameState,
    objective: GuideQuestObjective
): { current: number; target: number } {
    ensureGuideQuestState(state);

    if (objective.type === "flag") {
        return oneStepProgress(state.quests.flags[objective.flag]);
    }

    if (objective.type === "expeditionStarted") {
        return oneStepProgress(Boolean(state.run.currentExpedition.targetPlanet));
    }

    if (objective.type === "resourceLifetime") {
        return {
            current: getResourceLifetimeEarned(state, objective.resource),
            target: objective.target
        };
    }

    if (objective.type === "planetsDiscovered") {
        return { current: state.archive.discoveredPlanets.order.length, target: objective.target };
    }

    if (objective.type === "gradeCompleted") {
        return { current: getCompletedPlanetRecords(state).filter((record) => record.grade === objective.grade).length, target: objective.target };
    }

    if (objective.type === "specLevel") {
        return { current: state.run.probe.specs[objective.specKey].level, target: objective.level };
    }

    if (objective.type === "specsAtLevel") {
        const count = Object.values(state.run.probe.specs).filter((spec) => spec.level >= objective.level).length;
        return { current: count, target: objective.count };
    }

    if (objective.type === "researchCompleted") {
        return oneStepProgress(state.run.research.nodes[objective.nodeId]?.status === "completed");
    }

    if (objective.type === "researchCompletedCount") {
        return { current: getCompletedResearchCount(state), target: objective.target };
    }

    if (objective.type === "artifactTotal") {
        return { current: Object.keys(state.archive.artifactCollection.byInstanceId).length, target: objective.target };
    }

    if (objective.type === "artifactKey") {
        const summary = state.archive.artifactCollection.summaryByArtifactKey[objective.artifactKey];
        return { current: summary?.count || 0, target: objective.target };
    }

    if (objective.type === "artifactSameKind") {
        const maxCount = Math.max(
            0,
            ...Object.values(state.archive.artifactCollection.summaryByArtifactKey).map((summary) => summary?.count || 0)
        );
        return { current: maxCount, target: objective.target };
    }

    if (objective.type === "artifactGrade") {
        return { current: getArtifactCountFromGrade(state, objective.grade), target: objective.target };
    }

    if (objective.type === "artifactTypeCount") {
        const typeCount = Object.values(state.archive.artifactCollection.summaryByArtifactKey).filter((summary) => {
            return Boolean(summary && summary.count > 0);
        }).length;
        return { current: typeCount, target: objective.target };
    }

    if (objective.type === "artifactEachType") {
        const counts = (["ancient_ruins", "black_box", "crystal"] as ArtifactSlot["artifactKey"][]).map((key) => {
            return state.archive.artifactCollection.summaryByArtifactKey[key]?.count || 0;
        });
        return { current: Math.min(...counts), target: objective.target };
    }

    if (objective.type === "dangerSurvived") {
        const survived = getCompletedPlanetRecords(state).filter((record) => record.hazards.length > 0).length;
        return { current: survived, target: objective.target };
    }

    if (objective.type === "failedExpeditions") {
        const failed = Object.values(state.archive.discoveredPlanets.byId).filter((record) => record.outcome === "failed").length;
        return { current: failed, target: objective.target };
    }

    if (objective.type === "lifeSignal") {
        const found = getCompletedPlanetRecords(state).filter((record) => {
            if (objective.signal === "intelligent") {
                return record.lifeSignal === "intelligent";
            }

            return record.lifeSignal !== "none";
        }).length;
        return { current: found, target: objective.target };
    }

    if (objective.type === "xGradeUnlocked") {
        return oneStepProgress(state.prestige.permanentBonuses.xGradeUnlocked);
    }

    if (objective.type === "prestigeResetCount") {
        return { current: state.prestige.resetCount, target: objective.target };
    }

    if (objective.type === "starSystemReached") {
        return oneStepProgress(hasReachedStarSystem(state, objective.starSystemKey));
    }

    if (objective.type === "postPrestigePlanetClears") {
        return {
            current: state.prestige.resetCount > 0 ? state.prestige.currentStageProgress.clearedPlanetCount : 0,
            target: objective.target
        };
    }

    if (objective.type === "permanentBonusCount") {
        return { current: getPermanentBonusCount(state), target: objective.target };
    }

    if (objective.type === "permanentSpeedMultiplier") {
        return { current: state.prestige.permanentBonuses.explorationSpeedMultiplier, target: objective.target };
    }

    if (objective.type === "offlinePartsReward") {
        return { current: state.quests.stats.maxOfflinePartsReward, target: objective.target };
    }

    if (objective.type === "dailyUpgrades") {
        return { current: state.quests.stats.maxDailyUpgradeCount, target: objective.target };
    }

    if (objective.type === "researchTreeCompletions") {
        return { current: state.quests.stats.researchTreesCompleted, target: objective.target };
    }

    return { current: state.quests.stats.multiversePlanetsCompleted, target: objective.target };
}

function oneStepProgress(done: boolean): { current: number; target: number } {
    return { current: done ? 1 : 0, target: 1 };
}

function getResourceLifetimeEarned(state: GameState, resource: keyof ResourceAmounts): number {
    if (resource === "energy") {
        return state.run.resources.energy.lifetimeEarned;
    }

    return state.run.resources[resource].lifetimeEarned;
}

function getCompletedPlanetRecords(state: GameState): DiscoveredPlanetRecord[] {
    return Object.values(state.archive.discoveredPlanets.byId).filter((record) => record.outcome === "completed");
}

function getCompletedResearchCount(state: GameState): number {
    return Object.values(state.run.research.nodes).filter((node) => node.status === "completed").length;
}

function getArtifactCountFromGrade(state: GameState, grade: PlanetGrade): number {
    return Object.values(state.archive.artifactCollection.byInstanceId).filter((artifact) => {
        const sourcePlanet = state.archive.discoveredPlanets.byId[artifact.sourcePlanetId];
        return sourcePlanet?.grade === grade;
    }).length;
}

function hasReachedStarSystem(state: GameState, starSystemKey: string): boolean {
    if (state.prestige.currentStarSystemKey === starSystemKey) {
        return true;
    }

    if (state.prestige.completedStarSystems.includes(starSystemKey)) {
        return true;
    }

    const currentStage = getPrestigeDefinition(state.prestige.currentStage).stage;
    const targetStage = PRESTIGE_DEFINITIONS.find((definition) => definition.starSystemKey === starSystemKey)?.stage || Number.MAX_SAFE_INTEGER;
    return currentStage >= targetStage;
}

function getPermanentBonusCount(state: GameState): number {
    const bonuses = state.prestige.permanentBonuses;
    return [
        bonuses.explorationSpeedMultiplier > 1,
        bonuses.resourceGainMultiplier > 1,
        bonuses.artifactDiscoveryChanceBonus > 0,
        bonuses.xGradeUnlocked
    ].filter(Boolean).length;
}

function addResources(state: GameState, rewards: ResourceAmounts): ResourceAmounts {
    const applied = { ...EMPTY_AMOUNTS };
    const explorationData = Math.max(0, Math.floor(rewards.explorationData));
    const parts = Math.max(0, Math.floor(rewards.parts));
    const energy = Math.max(0, Math.floor(rewards.energy));

    if (explorationData > 0) {
        state.run.resources.explorationData.amount += explorationData;
        state.run.resources.explorationData.lifetimeEarned += explorationData;
        applied.explorationData = explorationData;
    }

    if (parts > 0) {
        state.run.resources.parts.amount += parts;
        state.run.resources.parts.lifetimeEarned += parts;
        applied.parts = parts;
    }

    if (energy > 0) {
        const current = state.run.resources.energy.current;
        const capacity = state.run.resources.energy.capacity;
        const accepted = Math.max(0, Math.min(energy, capacity - current));
        state.run.resources.energy.current += accepted;
        state.run.resources.energy.lifetimeEarned += accepted;
        state.run.resources.energy.lastRecoveredAt = state.run.currentExpedition.lastTickAt;
        applied.energy = accepted;
    }

    return applied;
}

function calculateResearchEffects(research: ResearchState): ResearchEffects {
    const effects = createEmptyResearchEffects();
    for (const id of RESEARCH_NODE_ORDER) {
        const node = research.nodes[id];
        if (!node || node.status !== "completed") {
            continue;
        }

        const definition = RESEARCH_DEFINITIONS[id];
        for (const effect of definition.effects) {
            if (effect.type === "resourceGainMultiplier") {
                effects.resourceGainMultiplier *= effect.value;
            } else if (effect.type === "explorationSpeedMultiplier") {
                effects.explorationSpeedMultiplier *= effect.value;
            } else if (effect.type === "deepSpaceUnlocked") {
                effects.deepSpaceUnlocked = effects.deepSpaceUnlocked || effect.value;
            } else if (effect.type === "lifeDiscoveryChanceBonus") {
                effects.lifeDiscoveryChanceBonus += effect.value;
            } else if (effect.type === "artifactDiscoveryChanceBonus") {
                effects.artifactDiscoveryChanceBonus += effect.value;
            }
        }
    }

    return {
        resourceGainMultiplier: roundTo(effects.resourceGainMultiplier, 4),
        explorationSpeedMultiplier: roundTo(effects.explorationSpeedMultiplier, 4),
        deepSpaceUnlocked: effects.deepSpaceUnlocked,
        lifeDiscoveryChanceBonus: roundTo(effects.lifeDiscoveryChanceBonus, 4),
        artifactDiscoveryChanceBonus: roundTo(effects.artifactDiscoveryChanceBonus, 4)
    };
}

function refreshResearchAvailability(state: GameState, nowIso: string): void {
    for (const id of RESEARCH_NODE_ORDER) {
        const node = state.run.research.nodes[id];
        if (node.status === "completed" || node.status === "researching") {
            continue;
        }

        const definition = RESEARCH_DEFINITIONS[id];
        const prerequisitesMet = definition.prerequisites.every((prerequisite) => {
            return state.run.research.nodes[prerequisite].status === "completed";
        });
        node.status = prerequisitesMet ? "available" : "locked";
        if (prerequisitesMet && !node.unlockedAt) {
            node.unlockedAt = nowIso;
        }
    }
}

function recalculateEffectiveStats(state: GameState): void {
    const specs = state.run.probe.specs;
    const research = state.run.research.activeEffects;
    const prestige = state.prestige.permanentBonuses;
    const engineMultiplier = 1 + specs.engine.level * 0.08;
    const gearMultiplier = 1 + specs.explorationGear.level * 0.1;

    state.run.probe.cachedEffectiveStats = {
        explorationSpeedMultiplier: roundTo(
            engineMultiplier * research.explorationSpeedMultiplier * prestige.explorationSpeedMultiplier,
            4
        ),
        resourceGainMultiplier: roundTo(
            gearMultiplier * research.resourceGainMultiplier * prestige.resourceGainMultiplier,
            4
        ),
        survivalPower: Math.floor(8 + specs.shield.level * 8),
        rareDiscoveryChanceBonus: clamp(
            0.04 + specs.antenna.level * 0.025 + research.artifactDiscoveryChanceBonus + prestige.artifactDiscoveryChanceBonus,
            0,
            0.95
        ),
        lifeDiscoveryChanceBonus: clamp(0.02 + specs.antenna.level * 0.03 + research.lifeDiscoveryChanceBonus, 0, 0.95),
        deepSpaceUnlocked: research.deepSpaceUnlocked
    };
}

function createEmptyResearchEffects(): ResearchEffects {
    return {
        resourceGainMultiplier: 1,
        explorationSpeedMultiplier: 1,
        deepSpaceUnlocked: false,
        lifeDiscoveryChanceBonus: 0,
        artifactDiscoveryChanceBonus: 0
    };
}

function updateExpectedCompletion(state: GameState): void {
    const expedition = state.run.currentExpedition;
    expedition.expectedCompletedAt = calculateExpectedCompletedAt(
        state,
        expedition.progress.required,
        expedition.progress.current,
        new Date(parseTime(expedition.lastTickAt, Date.now()))
    );
}

function calculateExpectedCompletedAt(state: GameState, required: number, current: number, from: Date): string {
    const progressRatePerMs = getProgressRatePerMs(state);
    const remaining = Math.max(0, required - current);
    const remainingMs = progressRatePerMs > 0 ? remaining / progressRatePerMs : 0;
    return new Date(from.getTime() + remainingMs).toISOString();
}

function getProgressRatePerMs(state: GameState): number {
    return (7 * state.run.probe.cachedEffectiveStats.explorationSpeedMultiplier) / 1000;
}

function getGradeWeights(state: GameState): Array<{ value: PlanetGrade; weight: number }> {
    const deepSpaceUnlocked = state.run.probe.cachedEffectiveStats.deepSpaceUnlocked;
    const xGradeUnlocked = state.prestige.permanentBonuses.xGradeUnlocked;
    return [
        { value: "F", weight: 42 },
        { value: "C", weight: 30 },
        { value: "B", weight: 16 },
        { value: "A", weight: deepSpaceUnlocked ? 9 : 5 },
        { value: "S", weight: deepSpaceUnlocked ? 3 : 1 },
        { value: "X", weight: xGradeUnlocked ? 1 : 0 }
    ];
}

function createHazards(rng: RandomSource, definition: PlanetGradeDefinition): PlanetHazard[] {
    if (definition.baseShieldRequirement <= 0) {
        return [];
    }

    const severity = Math.max(1, Math.ceil(rng() * definition.distanceTier));
    const hazardType = pickOne<PlanetHazard["type"]>(rng, ["radiation", "gravity_anomaly", "hostile_life"]);
    return [
        {
            type: hazardType,
            severity,
            requiredShieldPower: definition.baseShieldRequirement + severity * 2
        }
    ];
}

function createArtifactSlots(
    rng: RandomSource,
    definition: PlanetGradeDefinition,
    bonusChance: number
): ArtifactSlot[] {
    const chance = definition.grade === "S" ? 1 : clamp(definition.artifactChance + bonusChance, 0, 0.95);
    if (rng() > chance) {
        return [];
    }

    return [
        {
            artifactKey: pickOne(rng, ["ancient_ruins", "black_box", "crystal"]),
            rarity: definition.grade === "S" ? "legendary" : pickOne(rng, ["uncommon", "rare", "epic"]),
            discovered: false
        }
    ];
}

function rollLifeSignal(rng: RandomSource, bonusChance: number): TargetPlanet["lifeSignal"] {
    const microbialChance = clamp(0.08 + bonusChance * 0.5, 0, 0.7);
    const intelligentChance = clamp(0.015 + bonusChance * 0.2, 0, 0.3);
    const roll = rng();
    if (roll < intelligentChance) {
        return "intelligent";
    }

    if (roll < intelligentChance + microbialChance) {
        return "microbial";
    }

    return "none";
}

function applyPermanentBonuses(current: PrestigeBonuses, granted: Partial<PrestigeBonuses>): void {
    if (granted.explorationSpeedMultiplier) {
        current.explorationSpeedMultiplier = roundTo(current.explorationSpeedMultiplier * granted.explorationSpeedMultiplier, 4);
    }

    if (granted.resourceGainMultiplier) {
        current.resourceGainMultiplier = roundTo(current.resourceGainMultiplier * granted.resourceGainMultiplier, 4);
    }

    if (granted.artifactDiscoveryChanceBonus) {
        current.artifactDiscoveryChanceBonus = roundTo(
            current.artifactDiscoveryChanceBonus + granted.artifactDiscoveryChanceBonus,
            4
        );
    }

    if (granted.xGradeUnlocked) {
        current.xGradeUnlocked = true;
    }
}

function getPrestigeDefinition(stage: number): PrestigeDefinition {
    return PRESTIGE_DEFINITIONS.find((definition) => definition.stage === stage) ?? PRESTIGE_DEFINITIONS[PRESTIGE_DEFINITIONS.length - 1];
}

function createEmptySummary(): AdvanceSummary {
    return {
        planetsDiscovered: 0,
        resourcesGained: { ...EMPTY_AMOUNTS },
        artifactInstanceIds: []
    };
}

function mergeResourceAmounts(target: ResourceAmounts, source: ResourceAmounts): void {
    target.explorationData += source.explorationData;
    target.energy += source.energy;
    target.parts += source.parts;
}

function pickWeighted<T>(rng: RandomSource, items: Array<{ value: T; weight: number }>): T {
    const total = items.reduce((sum, item) => sum + Math.max(0, item.weight), 0);
    let roll = rng() * total;
    for (const item of items) {
        roll -= Math.max(0, item.weight);
        if (roll <= 0) {
            return item.value;
        }
    }

    return items[0].value;
}

function pickOne<T>(rng: RandomSource, items: readonly T[]): T {
    return items[Math.floor(rng() * items.length)];
}

function createSeed(now: Date): string {
    return `S-${formatDateKey(now)}-${Math.floor(Math.random() * 1679616).toString(36).toUpperCase()}`;
}

function createSeededRandom(seed: string): RandomSource {
    let value = 2166136261;
    for (let i = 0; i < seed.length; i++) {
        value ^= seed.charCodeAt(i);
        value = Math.imul(value, 16777619);
    }

    return () => {
        value += 0x6D2B79F5;
        let next = value;
        next = Math.imul(next ^ (next >>> 15), next | 1);
        next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
        return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
    };
}

function formatDateKey(date: Date): string {
    return date.toISOString().replace(/[-:.TZ]/g, "").slice(0, 14);
}

function parseTime(value: string | null | undefined, fallbackMs: number): number {
    if (!value) {
        return fallbackMs;
    }

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? fallbackMs : parsed;
}

function safeNumber(value: unknown): number {
    return typeof value === "number" && Number.isFinite(value) ? Math.max(0, value) : 0;
}

function roundTo(value: number, digits: number): number {
    const multiplier = Math.pow(10, digits);
    return Math.round(value * multiplier) / multiplier;
}

function trimCompactDecimal(value: number): string {
    return value.toFixed(value >= 100 ? 0 : 1).replace(/\.0$/, "");
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}
